import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  Routes,
  Route,
  Link,
  Outlet,
  NavLink,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import axios from "axios";
import faviconUrl from "./assets/branding/favicon.ico";
import appleTouchIconUrl from "./assets/branding/logo192.png";
import "./App.css";
import "./plugins/scrollLayout.css";
import "./plugins/phaseGridCell.css";
import "./plugins/dashboardMobile.css";
import "./plugins/boardThemes.css";
import "./plugins/boardThemesContent.css";
import "./plugins/workflowSchedule.css";
import ProjectDetailPage from "./ProjectDetailPage";
import PluginsPage from "./PluginsPage";
import AdminUsersPage from "./AdminUsersPage";
import SettingsPage from "./SettingsPage";
import CommentInboxPage from "./CommentInboxPage";
import { OutgoingMailLogGate } from "./OutgoingMailLogPage";
import { ProjectPhaseHistoryGate } from "./plugins/ProjectPhaseHistoryPage";
import { ConstructionBoardGate } from "./plugins/ConstructionBoardPage";
import { AddProjectGate } from "./plugins/AddProjectPage";
import { ParentWorkOrderGate } from "./plugins/ParentWorkOrderPage";
import { AddCommercialChildGate } from "./plugins/AddCommercialChildPage";
import { ExpansionSegmentGate } from "./plugins/ExpansionSegmentPage";
import { AddExpansionSubsegmentGate } from "./plugins/AddExpansionSubsegmentPage";
import InspectorDailyFootageModal from "./plugins/InspectorDailyFootageModal";
import { InspectorAddReportGate } from "./plugins/InspectorAddReportPage";
import { CommercialLocationsMapGate } from "./plugins/CommercialLocationsMapPage";
import PortfolioAiChatPanel from "./plugins/PortfolioAiChatPanel";
import MarketplaceCurrencyBadge from "./plugins/MarketplaceCurrencyBadge";
import { useBoardTheme, useBoardThemeLoader } from "./plugins/useEquippedBoardTheme";
import BoardProjectSearch from "./plugins/BoardProjectSearch";
import { rowMatchesProjectSearch, normalizeProjectSearchQuery } from "./plugins/boardProjectSearch";
import { MarketplaceShopGate } from "./plugins/MarketplaceShopPage";
import { ContractsGate } from "./plugins/ContractsPage";
import { canViewContracts } from "./plugins/contractsAccess";
import { CrmGate } from "./plugins/CrmPage";
import { canViewCrm } from "./plugins/crmAccess";
import { InterconnectsGate } from "./plugins/InterconnectsPage";
import { ReportingGate } from "./plugins/ReportingPage";
import { MaterialsInventoryGate } from "./plugins/MaterialsInventoryPage";
import { DatacenterLayoutGate } from "./plugins/DatacenterPage";
import { canAccessDatacenterLayout } from "./plugins/datacenterLayoutAccess";
import { canUsePortfolioAi } from "./plugins/portfolioAiAccess";
import {
  canAccessProposalBuilder,
  canCreateCommercialProject,
  canEditEngineeringTabFields,
  canEditPermittingTabFields,
  canViewFullExpansionConstructionContractFields,
} from "./plugins/commercialRoleAccess";
import {
  buildPermittingTrackingFieldPatch,
  PERMITTING_TRACKING_FIELD_KEYS,
} from "./plugins/commercialPermitsSchema";
import {
  buildExpansionFootageFieldPatch,
  EXPANSION_FOOTAGE_GRID_FIELD_KEYS,
} from "./plugins/expansionProjectForms";
import { canAccessReporting } from "./plugins/reportingAccess";
import { canAccessMaterialsInventory } from "./plugins/materialsInventoryAccess";
import { canViewInterconnects } from "./plugins/interconnectsAccess";
import { canAccessInspectorAddReport } from "./plugins/expansionInspectorAccess";
import { canAccessShop } from "./plugins/shopAccess";
import { canAccessMessages } from "./plugins/messagesAccess";
import {
  canAccessScheduleCalendar,
  canAccessCommercialMap,
  canAccessSettings,
} from "./plugins/appChromeAccess";
import {
  buildPluginsHeader,
  clearPluginsHydration,
  hydratePluginsFromServer,
  PLUGIN_IDS,
  isPluginEnabled,
} from "./plugins/registry";
import {
  applyUserMatrixColumnVisibility,
  canViewerAccessPanel,
  canViewerPermission,
  clearUserMatrixHydration,
  getImpersonation,
  getAllowedColumnIdsForViewer,
  getViewerEmailFromStorage,
  hasEffectiveAdminPrivileges,
  hydrateUserMatrixFromServer,
  hydrateViewerSessionFromServer,
  isImpersonating,
  isViewerAdministrator,
  stopImpersonation,
} from "./plugins/userMatrixStorage";
import {
  hydrateEmailTriggersFromServer,
  clearEmailTriggersHydration,
} from "./plugins/emailTriggersStorage";
import {
  hydrateEngineeringMaterialsCatalogFromServer,
  clearEngineeringMaterialsCatalogHydration,
} from "./plugins/engineeringMaterialsCatalog";
import {
  clearCommercialGridDefaultsHydration,
  getCommercialGridDefaultFilterModelForGrid,
  hydrateCommercialGridDefaultsFromServer,
} from "./plugins/commercialGridDefaultsStorage";
import {
  clearAllBoardGridViewState,
  clearBoardGridFilterState,
  loadBoardGridViewState,
  patchBoardGridViewState,
} from "./plugins/boardGridViewState";
import { fetchAppConfig, fetchInvitePreview, postAppConfigSessionInit } from "./plugins/appConfigApi";
import { postForgotPassword, postResetPassword } from "./plugins/authApi";
import { emitMarketplaceRewardFromResponse } from "./plugins/marketplaceRewards";
import { getApiBase } from "./apiBase";
import {
  buildSubWorkOrderCountByBase,
  commercialWorkOrderGroupPath,
} from "./plugins/commercialWorkOrderGroup";
import { readStoredAdminFlag, writeStoredAdminFlag } from "./authAdmin";
import {
  ACF,
  autoSizeContentFitGridColumns,
  buildExpansionColumnDefs,
  buildProjectColumnDefs,
  EXPANSION_GROUP_ROLLUP_COL_IDS,
  formatExpansionWorkOrderForDisplay,
  isCvinExpansion,
  isMultisiteParentRow,
  phaseLabelForActiveSubphase,
} from "./projectGridColumns";
import { readSubsegmentSummaryFromAcf } from "./plugins/expansionProjectAcf";
import { fetchBatchProjectContractFields } from "./plugins/contractsApi";
import { filterProjectsForContractorViewer } from "./plugins/contractorProjectAccess";
import { computeSubsegmentAwardRollups } from "./plugins/expansionSegmentDetailExport";
import {
  computeExpansionGroupStats,
  formatExpansionGroupTitle,
  segmentFromSubsegmentPrefix,
} from "./plugins/expansionGroupUtils";
import {
  displayPhaseForRow,
  phaseCellClass,
  phaseCellStyle,
  subphaseCellStyle,
} from "./projectPhaseDisplay";
import projectBoardLogo from "./assets/project-board-logo.png";
import projectBoardLogoDark from "./assets/project-board-logo-dark.png";

import {
  ModuleRegistry,
  AllCommunityModule,
} from "ag-grid-community";
import { boardAgGridThemeLight } from "./plugins/boardAgGridThemes";

ModuleRegistry.registerModules([AllCommunityModule]);

function applyBrandingIcons() {
  if (typeof document === "undefined") return;
  const ensureLink = (rel, href, attrs = {}) => {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", rel);
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  };
  ensureLink("icon", faviconUrl, { type: "image/x-icon" });
  ensureLink("apple-touch-icon", appleTouchIconUrl);
}
applyBrandingIcons();

/** Commercial grid only: hidden by default (still unhideable via header context menu). */
const COMMERCIAL_DEFAULT_HIDDEN_COL_IDS = new Set([
  "id",
  "legacy_wp_post_id",
  "work_order_base",
  "export_slug",
  "department",
  "site_name",
  "a_location",
  "z_location",
]);

/** Columns inserted by Materials plugin + role permission — always shown when present. */
const MATERIALS_BOARD_COL_IDS = new Set([
  "materials_summary",
  "materials_review",
  "exp_materials_summary",
  "exp_materials_review",
]);

function forceMaterialsBoardColumnsVisible(defs) {
  return (defs || []).map((col) => {
    const id = String(col.colId ?? col.field ?? "");
    if (!MATERIALS_BOARD_COL_IDS.has(id)) return col;
    return { ...col, hide: false };
  });
}

/** Push `hide` flags from column defs onto a live grid (AG Grid may not re-apply hide on defs-only updates). */
function syncGridColumnVisibility(api, columnDefs) {
  if (!api || !Array.isArray(columnDefs)) return;
  const toShow = [];
  const toHide = [];
  for (const col of columnDefs) {
    const id = col.colId ?? col.field;
    if (!id || id === "__select__") continue;
    if (col.hide === true) toHide.push(id);
    else toShow.push(id);
  }
  if (toShow.length) api.setColumnsVisible(toShow, true);
  if (toHide.length) api.setColumnsVisible(toHide, false);
}

function hasActiveFilterModel(model) {
  return Boolean(model && typeof model === "object" && Object.keys(model).length > 0);
}

function persistGridFilterModel(panel, api) {
  if (!api?.getFilterModel) return;
  patchBoardGridViewState(panel, {
    filterModel: api.getFilterModel() || null,
    filtersInitialized: true,
  });
}

function persistGridSortState(panel, api) {
  if (!api?.getColumnState) return;
  const sortState =
    api
      .getColumnState()
      .filter((c) => c.sort != null)
      .map((c) => ({ colId: c.colId, sort: c.sort, sortIndex: c.sortIndex }))
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0)) || [];
  patchBoardGridViewState(panel, { sortState });
}

function applyCommercialGridDefaultFilters(api) {
  if (!api) return;
  const model = getCommercialGridDefaultFilterModelForGrid();
  api.setFilterModel(model || null);
  patchBoardGridViewState("commercial", {
    filterModel: model || null,
    filtersInitialized: true,
  });
}

function restoreOrApplyCommercialFilters(api) {
  if (!api) return;
  const saved = loadBoardGridViewState("commercial");
  if (saved?.filtersInitialized) {
    api.setFilterModel(saved.filterModel || null);
    return;
  }
  applyCommercialGridDefaultFilters(api);
}

function restoreOrApplyCommercialSort(api) {
  if (!api?.applyColumnState) return;
  const saved = loadBoardGridViewState("commercial");
  if (Array.isArray(saved?.sortState) && saved.sortState.length) {
    api.applyColumnState({
      state: saved.sortState,
      defaultState: { sort: null },
    });
    return;
  }
  const defaultSort = [{ colId: "work_order", sort: "desc" }];
  api.applyColumnState({
    state: defaultSort,
    defaultState: { sort: null },
  });
  patchBoardGridViewState("commercial", { sortState: defaultSort });
}

function restoreOrApplyExpansionFilters(api) {
  if (!api) return;
  const saved = loadBoardGridViewState("expansion");
  if (saved?.filtersInitialized) {
    api.setFilterModel(saved.filterModel || null);
    return;
  }
  patchBoardGridViewState("expansion", {
    filterModel: null,
    filtersInitialized: true,
  });
}

function restoreOrApplyExpansionSort(api) {
  if (!api?.applyColumnState) return;
  const saved = loadBoardGridViewState("expansion");
  if (Array.isArray(saved?.sortState) && saved.sortState.length) {
    api.applyColumnState({
      state: saved.sortState,
      defaultState: { sort: null },
    });
    return;
  }
  const defaultSort = [{ colId: "exp_segment", sort: "asc" }];
  api.applyColumnState({
    state: defaultSort,
    defaultState: { sort: null },
  });
  patchBoardGridViewState("expansion", { sortState: defaultSort });
}

/** Expansion grid: one row group per parent work order (`work_order_base`), not per sub-WO. */
const EXPANSION_GROUP_BY = "expansion_wo_base";

function expansionStatsGroup(row) {
  const subSummary = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
  if (subSummary && typeof subSummary === "object") {
    return {
      construction_footage: subSummary.construction_footage,
      override_footage: subSummary.override_footage,
      completed_footage: subSummary.completed_footage,
      caltrans_footage: subSummary.caltrans_footage,
      handholes: subSummary.handholes,
      waterway: subSummary.waterway,
    };
  }
  const j = row?.acf_groups_json;
  if (!j || typeof j !== "object") return null;
  return j[ACF.EXPANSION] ?? j.expansion_segment_statistics ?? null;
}

function parseFootageNumber(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).replace(/,/g, "").trim();
  const m = s.match(/[\d.]+/);
  if (!m) return null;
  const n = parseFloat(m[0]);
  return Number.isFinite(n) ? n : null;
}

function workOrderBaseForRow(row) {
  if (row.work_order_base != null && String(row.work_order_base).trim() !== "") {
    return String(row.work_order_base).trim();
  }
  const wo = String(row.work_order ?? "").trim();
  const dot = wo.indexOf(".");
  if (dot >= 0) return wo.slice(0, dot).trim();
  return wo || "";
}

function expansionGroupKeyForRow(row) {
  const base = workOrderBaseForRow(row);
  return base ? base : "(No WO base)";
}

function expansionParentWoSortCompare(keyA, keyB) {
  const sa = segmentSortValue(keyA);
  const sb = segmentSortValue(keyB);
  if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb) return sa - sb;
  if (Number.isFinite(sa) && !Number.isFinite(sb)) return -1;
  if (!Number.isFinite(sa) && Number.isFinite(sb)) return 1;
  return String(keyA).localeCompare(String(keyB), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function compareExpansionSubsegmentRows(a, b) {
  const woA = formatExpansionWorkOrderForDisplay(a);
  const woB = formatExpansionWorkOrderForDisplay(b);
  return woA.localeCompare(woB, undefined, { numeric: true, sensitivity: "base" });
}

/** Project data rows currently shown in the grid (search + column filters). */
function forEachVisibleProjectDataNode(api, callback) {
  api?.forEachNodeAfterFilter?.((node) => {
    if (node.data?._rowType !== "group" && node.data?.id != null) {
      callback(node);
    }
  });
}

/** Segment group header rows currently shown (includes collapsed groups). */
function forEachVisibleExpansionGroupNode(api, callback) {
  api?.forEachNodeAfterFilter?.((node) => {
    if (
      node.data?._rowType === "group" &&
      node.data?._groupBy === EXPANSION_GROUP_BY &&
      node.data?._groupKey != null
    ) {
      callback(node);
    }
  });
}

function getVisibleSelectedProjectRows(api) {
  const rows = [];
  forEachVisibleProjectDataNode(api, (node) => {
    if (node.isSelected?.()) rows.push(node.data);
  });
  return rows;
}

function countVisibleSelectedProjects(api) {
  return getVisibleSelectedProjectRows(api).length;
}

function getVisibleExpansionGroupKeys(api) {
  const keys = [];
  forEachVisibleExpansionGroupNode(api, (node) => {
    keys.push(node.data._groupKey);
  });
  return keys;
}

function isExpansionGroupFullySelected(api, groupKey, selectedGroupKeys) {
  if (selectedGroupKeys?.has(groupKey)) return true;
  let hasVisible = false;
  let allSelected = true;
  forEachVisibleProjectDataNode(api, (node) => {
    if (groupLabelForRow(node.data, EXPANSION_GROUP_BY) !== groupKey) return;
    hasVisible = true;
    if (!node.isSelected?.()) allSelected = false;
  });
  return hasVisible && allSelected;
}

function isExpansionGroupPartiallySelected(api, groupKey, selectedGroupKeys) {
  if (selectedGroupKeys?.has(groupKey)) return false;
  let hasSelected = false;
  let hasUnselected = false;
  forEachVisibleProjectDataNode(api, (node) => {
    if (groupLabelForRow(node.data, EXPANSION_GROUP_BY) !== groupKey) return;
    if (node.isSelected?.()) hasSelected = true;
    else hasUnselected = true;
  });
  return hasSelected && hasUnselected;
}

function shouldSortExpansionChildrenByWorkOrder(primarySort) {
  const colId = primarySort?.colId;
  if (!colId) return true;
  return colId === "exp_segment" || colId === "work_order";
}

function formatExpansionFootage(n) {
  if (n == null || !Number.isFinite(n)) return "";
  return `${Math.round(n).toLocaleString()} ft`;
}

function formatExpansionPercent(n) {
  if (n == null || !Number.isFinite(n)) return "";
  return `${n}%`;
}

/** Community row grouping: bucket key for a project row (not a synthetic group header). */
function groupLabelForRow(row, groupByKey) {
  if (groupByKey === EXPANSION_GROUP_BY) {
    return expansionGroupKeyForRow(row);
  }
  if (groupByKey === "work_order_base") {
    return row.work_order_base != null && String(row.work_order_base).trim() !== ""
      ? String(row.work_order_base).trim()
      : "(No WO base)";
  }
  return "(Other)";
}

function segmentSortValue(label) {
  const s = String(label || "").trim();
  const m1 = s.match(/\bsegment\s*[-#:]?\s*(\d+)/i);
  if (m1) return Number(m1[1]);
  const m2 = s.match(/\b(\d+)\b/);
  if (m2) return Number(m2[1]);
  return Number.POSITIVE_INFINITY;
}

function buildGroupedDisplayRows(flatRows, groupByKey, expandedKeys, options = {}) {
  const { contractFieldsByProjectId = null } = options;
  if (!groupByKey) {
    return flatRows.map((r) => ({ ...r, _rowType: "data" }));
  }
  const buckets = new Map();
  for (const row of flatRows) {
    const label = groupLabelForRow(row, groupByKey);
    if (!buckets.has(label)) buckets.set(label, []);
    buckets.get(label).push(row);
  }
  const sortedLabels = [...buckets.keys()].sort((a, b) => {
    if (groupByKey === EXPANSION_GROUP_BY) return expansionParentWoSortCompare(a, b);
    const sa = segmentSortValue(a);
    const sb = segmentSortValue(b);
    if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb) return sa - sb;
    if (Number.isFinite(sa) && !Number.isFinite(sb)) return -1;
    if (!Number.isFinite(sa) && Number.isFinite(sb)) return 1;
    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
  const out = [];
  for (const label of sortedLabels) {
    const children = buckets.get(label);
    const isExpanded = expandedKeys.has(label);
    const safeId = encodeURIComponent(label);
    const groupRow = {
      _rowType: "group",
      _groupKey: label,
      _groupBy: groupByKey,
      _childCount: children.length,
      _expanded: isExpanded,
      _groupRowId: `group:${groupByKey}:${safeId}`,
    };
    if (groupByKey === EXPANSION_GROUP_BY) {
      const woBase = label.startsWith("(No ") ? "" : label;
      const segment = segmentFromSubsegmentPrefix(children);
      groupRow.work_order_base = woBase;
      groupRow._groupWoBase = woBase;
      groupRow._groupSegment = segment;
      groupRow._groupStats = computeExpansionGroupStats(children);
      groupRow._groupTitle = formatExpansionGroupTitle(woBase, segment);
      groupRow._groupAwardRollups = computeSubsegmentAwardRollups(
        children,
        contractFieldsByProjectId
      );
    }
    if (groupByKey === "work_order_base") {
      groupRow.work_order_base = label.startsWith("(No ") ? "" : label;
    }
    out.push(groupRow);
    if (isExpanded) {
      const sortedChildren = [...children].sort(compareExpansionSubsegmentRows);
      for (const r of sortedChildren) {
        out.push({ ...r, _rowType: "data" });
      }
    }
  }
  return out;
}

/** Span group title across columns until the first rollup column (conduit/fiber/awarded, etc.). */
function expansionGroupColSpan(params) {
  if (params.data?._rowType !== "group") return 1;
  const cols = params.api?.getAllDisplayedColumns?.() ?? [];
  if (!cols.length) return 1;
  const idx = cols.findIndex((c) => c.getColId() === params.column.getColId());
  if (idx < 0) return 1;
  let end = cols.length;
  for (let i = idx + 1; i < cols.length; i += 1) {
    const id = cols[i].getColId();
    if (EXPANSION_GROUP_ROLLUP_COL_IDS.has(id)) {
      end = i;
      break;
    }
  }
  return Math.max(1, end - idx);
}

function formatExpansionGroupStatsLine(stats, awardRollups, showAwardRollups) {
  if (!stats) return "";
  const n = stats.projectCount ?? 0;
  const parts = [`${n} subsegment${n === 1 ? "" : "s"}`];
  if (showAwardRollups && awardRollups) {
    if (awardRollups.awardedFootage != null) {
      parts.push(`${formatExpansionFootage(awardRollups.awardedFootage)} awarded`);
    }
    if (awardRollups.awardedContractorCost != null) {
      parts.push(
        awardRollups.awardedContractorCost.toLocaleString(undefined, {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        })
      );
    }
  }
  if (stats.percentComplete != null) {
    parts.push(formatExpansionPercent(stats.percentComplete) + " complete");
  }
  if (stats.overrideFootageTotal != null) {
    parts.push(`${formatExpansionFootage(stats.overrideFootageTotal)} override`);
  }
  if (stats.constructionFootageTotal != null) {
    parts.push(`${formatExpansionFootage(stats.constructionFootageTotal)} construction`);
  }
  return parts.join(" · ");
}

const GroupRowCellRenderer = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    refresh: () => true,
  }));
  const d = props.data;
  const ctx = props.context;
  if (!d || d._rowType !== "group") return null;

  const isExpansion = d._groupBy === EXPANSION_GROUP_BY;
  const title = isExpansion ? d._groupTitle || d._groupKey : d._groupKey;
  const showAwardRollups = !!ctx?.showContractGroupCols;
  const statsLine = isExpansion
    ? formatExpansionGroupStatsLine(d._groupStats, d._groupAwardRollups, showAwardRollups)
    : null;

  const expansionHeaderStyle = isExpansion
    ? { alignItems: "flex-start", padding: "8px 12px", gap: 8 }
    : undefined;
  const expansionTextStyle = isExpansion
    ? { display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }
    : undefined;
  return (
    <button
      type="button"
      className={`group-row-header${isExpansion ? " group-row-header--expansion" : ""}`}
      style={expansionHeaderStyle}
      onClick={() => ctx?.toggleGroupKey?.(d._groupKey)}
    >
      <span className="group-row-chevron" aria-hidden>
        {d._expanded ? "▼" : "▶"}
      </span>
        <span className="group-row-header-text" style={expansionTextStyle}>
        <span className="group-row-label">
          {isExpansion && d._groupWoBase && !String(d._groupKey || "").startsWith("(") ? (
            <Link
              to={`/expansion/segment/${encodeURIComponent(d._groupWoBase)}`}
              className="group-row-segment-link"
              onClick={(e) => e.stopPropagation()}
            >
              {title}
            </Link>
          ) : (
            title
          )}
        </span>
        {isExpansion && statsLine ? (
          <span className="group-row-stats">
            {statsLine}
          </span>
        ) : (
          <span className="group-row-count"> ({d._childCount})</span>
        )}
      </span>
    </button>
  );
});
GroupRowCellRenderer.displayName = "GroupRowCellRenderer";

function ExpansionSelectAllHeader(params) {
  const ctx = params.context;
  const api = params.api;
  void ctx?.expansionSelectionRevision;
  const selectedGroupKeys = ctx?.selectedExpansionGroupKeys;
  const visibleKeys = getVisibleExpansionGroupKeys(api);
  const allSelected =
    visibleKeys.length > 0 &&
    visibleKeys.every((k) => isExpansionGroupFullySelected(api, k, selectedGroupKeys));
  const someSelected =
    !allSelected &&
    visibleKeys.some(
      (k) =>
        isExpansionGroupFullySelected(api, k, selectedGroupKeys) ||
        isExpansionGroupPartiallySelected(api, k, selectedGroupKeys)
    );
  return (
    <input
      type="checkbox"
      ref={(el) => {
        if (el) el.indeterminate = someSelected;
      }}
      checked={allSelected}
      aria-label="Select all visible expansion projects and subsegments"
      title="Select all visible segments and subsegments (including collapsed)"
      style={{ width: 16, height: 16, cursor: "pointer", margin: "0 auto", display: "block" }}
      onChange={(e) => ctx?.toggleAllExpansionGroups?.(e.target.checked)}
    />
  );
}

function ExpansionGroupSelectCellRenderer(params) {
  const d = params.data;
  const ctx = params.context;
  const api = params.api;
  if (!d || d._rowType !== "group" || d._groupBy !== EXPANSION_GROUP_BY) return null;
  const title = d._groupTitle || d._groupKey;
  void ctx?.expansionSelectionRevision;
  const selectedGroupKeys = ctx?.selectedExpansionGroupKeys;
  const checked = isExpansionGroupFullySelected(api, d._groupKey, selectedGroupKeys);
  const indeterminate =
    !checked && isExpansionGroupPartiallySelected(api, d._groupKey, selectedGroupKeys);
  return (
    <input
      type="checkbox"
      ref={(el) => {
        if (el) el.indeterminate = indeterminate;
      }}
      checked={checked}
      aria-label={`Select all subsegments in ${title}`}
      title={`Select all subsegments in ${title} (including when collapsed)`}
      style={{ width: 16, height: 16, cursor: "pointer", margin: 0 }}
      onChange={(e) => {
        e.stopPropagation();
        ctx?.toggleExpansionGroupSelection?.(d._groupKey, e.target.checked);
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function csvEscapeCell(val) {
  if (val == null) return "";
  const s = String(val);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Export rows using visible grid columns (matches on-screen table). */
function getExportColumnDefs(api, fallbackColumnDefs) {
  const displayed = api?.getAllDisplayedColumns?.() ?? [];
  if (displayed.length) {
    return displayed
      .map((col) => ({
        ...col.getColDef(),
        colId: col.getColId(),
        field: col.getColDef().field,
      }))
      .filter((def) => {
        const id = def.colId ?? def.field;
        return id && id !== "__select__" && id !== "__expansion_group__";
      });
  }
  return (fallbackColumnDefs || []).filter((c) => {
    const id = c.colId ?? c.field;
    return id && id !== "__select__" && id !== "__expansion_group__" && c.hide !== true;
  });
}

function exportProjectRowsToCsv(rows, columnDefs, gridContext, filename = "selected-projects.csv") {
  const cols = (columnDefs || []).filter(
    (c) => c.colId !== "__select__" && c.colId !== "__expansion_group__" && (c.field || typeof c.valueGetter === "function")
  );
  const headerLine = cols
    .map((c) => csvEscapeCell(c.headerName || c.field || c.colId || ""))
    .join(",");
  const lines = [headerLine];
  for (const row of rows) {
    const cells = cols.map((c) => {
      let v;
      if (typeof c.valueGetter === "function") {
        try {
          v = c.valueGetter({ data: row, context: gridContext });
        } catch {
          v = "";
        }
      } else if (c.field) {
        v = row[c.field];
      } else {
        v = "";
      }
      if (typeof c.valueFormatter === "function") {
        try {
          v = c.valueFormatter({ value: v, data: row, context: gridContext });
        } catch {
          /* keep raw value */
        }
      }
      return csvEscapeCell(v);
    });
    lines.push(cells.join(","));
  }
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function buildExpansionExportRows(selectedDataRows, selectedGroupKeys, displayRows) {
  const groupKeys = new Set();
  for (const row of selectedDataRows) {
    groupKeys.add(expansionGroupKeyForRow(row));
  }
  for (const key of selectedGroupKeys || []) {
    groupKeys.add(key);
  }
  const sortedKeys = [...groupKeys].sort((a, b) => expansionParentWoSortCompare(a, b));
  const exportRows = [];
  const usedChildIds = new Set();
  for (const key of sortedKeys) {
    const groupRow = displayRows.find(
      (r) => r._rowType === "group" && r._groupKey === key && r._groupBy === EXPANSION_GROUP_BY
    );
    const children = selectedDataRows.filter((r) => expansionGroupKeyForRow(r) === key);
    if (groupRow && children.length) {
      exportRows.push(groupRow);
      for (const child of children) {
        exportRows.push(child);
        usedChildIds.add(child.id);
      }
    } else if (children.length) {
      for (const child of children) {
        exportRows.push(child);
        usedChildIds.add(child.id);
      }
    }
  }
  for (const row of selectedDataRows) {
    if (!usedChildIds.has(row.id)) exportRows.push(row);
  }
  return exportRows;
}

const AUTH_TOKEN_KEY = "projectBoardAuthToken";
const AUTH_USER_EMAIL_KEY = "projectBoardAuthUserEmail";
const AUTH_FLASH_MESSAGE_KEY = "projectBoardAuthFlashMessage";
const ALLOWED_SIGNUP_DOMAINS = ["cvin.com", "vastnetworks.com", "calore.net"];
const IDLE_PASSWORD_RESET_CODE = "IDLE_PASSWORD_RESET_REQUIRED";

function readAuthFlashMessage() {
  try {
    const msg = sessionStorage.getItem(AUTH_FLASH_MESSAGE_KEY);
    if (msg) sessionStorage.removeItem(AUTH_FLASH_MESSAGE_KEY);
    return msg || "";
  } catch {
    return "";
  }
}

function writeAuthFlashMessage(msg) {
  try {
    if (msg) sessionStorage.setItem(AUTH_FLASH_MESSAGE_KEY, String(msg));
  } catch {
    /* ignore */
  }
}

function SidebarLogoutButton({ onLogout }) {
  return (
    <button
      type="button"
      className="app-sidebar-logout"
      onClick={onLogout}
      title="Log out"
      aria-label="Log out"
    >
      <svg
        className="app-sidebar-logout-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    </button>
  );
}

function SidebarGlobeButton() {
  return (
    <NavLink
      to="/commercial-map"
      className={({ isActive }) =>
        `app-sidebar-icon-button app-sidebar-globe${isActive ? " is-active" : ""}`
      }
      title="Commercial project map"
      aria-label="Commercial project map"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    </NavLink>
  );
}

function SidebarCalendarButton() {
  return (
    <NavLink
      to="/construction"
      className={({ isActive }) =>
        `app-sidebar-icon-button app-sidebar-calendar${isActive ? " is-active" : ""}`
      }
      title="Schedule calendar"
      aria-label="Schedule calendar"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    </NavLink>
  );
}

function SidebarSettingsButton() {
  return (
    <NavLink
      to="/settings"
      className={({ isActive }) =>
        `app-sidebar-icon-button app-sidebar-settings${isActive ? " is-active" : ""}`
      }
      title="Settings"
      aria-label="Settings"
    >
      <svg
        className="app-sidebar-settings-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82L4.21 7.2a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.08a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.08a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.08a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    </NavLink>
  );
}

function DashboardLayout({
  mainView,
  setMainView,
  headerMenu,
  hideColumn,
  unhideColumn,
  hiddenColumns,
  logout,
  showMailLogNav,
  showPhaseHistoryNav,
  showMessagesNav,
  showPluginsNav,
  showAdminUsersNav,
  canAccessCommercial,
  canAccessExpansion,
  showConstructionCalendar,
  showCommercialMap,
  showSettings,
  showAddProjectNav,
  showProposalNav,
  showShopNav,
  showContractsNav,
  showCrmNav,
  showInterconnectsNav,
  showReportingNav,
  showMaterialsInventoryNav,
  showDatacenterNav,
  showAddReportNav,
  impersonationBanner,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const pluginsActive = location.pathname === "/plugins";
  const mailLogActive = location.pathname === "/mail-log";
  const phaseHistoryActive = location.pathname === "/admin/phase-history";
  const adminUsersActive = location.pathname === "/admin/users";
  const settingsActive = location.pathname === "/settings";
  const messagesActive = location.pathname === "/messages";
  const constructionActive = location.pathname === "/construction";
  const commercialMapActive = location.pathname === "/commercial-map";
  const addProjectActive = location.pathname === "/add-project";
  const addReportActive = location.pathname === "/add-report";
  const shopActive = location.pathname === "/shop";
  const contractsActive = location.pathname.startsWith("/contracts");
  const crmActive = location.pathname.startsWith("/crm");
  const interconnectsActive = location.pathname.startsWith("/interconnects");
  const reportingActive = location.pathname === "/reporting";
  const materialsInventoryActive = location.pathname === "/materials-inventory";
  const datacenterActive = location.pathname === "/datacenter";
  const projectDetailActive = location.pathname.startsWith("/project/");
  const workOrderGroupActive = location.pathname.startsWith("/work-order/");
  const expansionSegmentActive = location.pathname.startsWith("/expansion/segment/");
  const secondaryRoute =
    pluginsActive ||
    mailLogActive ||
    phaseHistoryActive ||
    adminUsersActive ||
    settingsActive ||
    messagesActive ||
    constructionActive ||
    commercialMapActive ||
    addProjectActive ||
    addReportActive ||
    shopActive ||
    contractsActive ||
    crmActive ||
    interconnectsActive ||
    reportingActive ||
    materialsInventoryActive ||
    datacenterActive ||
    projectDetailActive ||
    workOrderGroupActive ||
    expansionSegmentActive;

  const goCommercial = useCallback(() => {
    setMainView("commercial");
    if (secondaryRoute) navigate("/");
  }, [navigate, secondaryRoute, setMainView]);

  const goExpansion = useCallback(() => {
    setMainView("expansion");
    if (secondaryRoute) navigate("/");
  }, [navigate, secondaryRoute, setMainView]);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const closeMobileNav = useCallback(() => {
    setMobileNavOpen(false);
  }, []);

  const toggleMobileNav = useCallback(() => {
    setMobileNavOpen((open) => !open);
  }, []);

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const goCommercialMobile = useCallback(() => {
    goCommercial();
    closeMobileNav();
  }, [goCommercial, closeMobileNav]);

  const goExpansionMobile = useCallback(() => {
    goExpansion();
    closeMobileNav();
  }, [goExpansion, closeMobileNav]);

  const boardNavActive = !secondaryRoute;
  useBoardThemeLoader();
  const { themeClass: boardThemeClass, isLegendary: boardThemeLegendary } = useBoardTheme();
  const brandLogoSrc = boardThemeLegendary ? projectBoardLogoDark : projectBoardLogo;

  return (
    <div
      className={`app-shell app-shell-dashboard${boardThemeClass ? ` ${boardThemeClass}` : ""}${
        boardThemeLegendary ? " pb-board-theme-legendary" : ""
      }`}
    >
      <MarketplaceCurrencyBadge />
      <button
        type="button"
        className={`app-mobile-nav-backdrop${mobileNavOpen ? " is-visible" : ""}`}
        aria-hidden={!mobileNavOpen}
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={closeMobileNav}
      />
      <header className="app-mobile-topbar">
        <button
          type="button"
          className="app-mobile-nav-toggle"
          aria-expanded={mobileNavOpen}
          aria-controls="app-sidebar-nav-panel"
          onClick={toggleMobileNav}
        >
          <span className="app-mobile-nav-toggle-bar" aria-hidden />
          <span className="app-mobile-nav-toggle-bar" aria-hidden />
          <span className="app-mobile-nav-toggle-bar" aria-hidden />
          <span className="app-mobile-nav-toggle-label">Menu</span>
        </button>
        <img src={brandLogoSrc} alt="Project Board" className="app-mobile-topbar-logo" />
      </header>
      <div className="app-layout">
        <aside
          id="app-sidebar-nav-panel"
          className={`app-sidebar${mobileNavOpen ? " is-open" : ""}`}
          aria-label="Main navigation"
        >
          <div className="app-sidebar-main">
            <h1 className="app-sidebar-title app-sidebar-title--desktop">
              <img src={brandLogoSrc} alt="Project Board" className="app-sidebar-logo" />
            </h1>
            <nav
              className="app-sidebar-nav"
              aria-label="Main navigation"
              onClick={closeMobileNav}
            >
              {canAccessCommercial ? (
                <button
                  type="button"
                  className={`app-nav-button${boardNavActive && mainView === "commercial" ? " is-active" : ""}`}
                  onClick={goCommercialMobile}
                >
                  Commercial
                </button>
              ) : null}
              {canAccessExpansion ? (
                <button
                  type="button"
                  className={`app-nav-button${boardNavActive && mainView === "expansion" ? " is-active" : ""}`}
                  onClick={goExpansionMobile}
                >
                  Expansion
                </button>
              ) : null}
              {showProposalNav ? (
                <a href="/proposal" className="app-nav-button">
                  Proposals
                </a>
              ) : null}
              {showAddProjectNav ? (
                <NavLink
                  to="/add-project"
                  className={({ isActive }) =>
                    `app-nav-button app-nav-button--add-project${isActive ? " is-active" : ""}`
                  }
                >
                  Add project
                </NavLink>
              ) : null}
              {showAddReportNav ? (
                <NavLink
                  to="/add-report"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Add Report
                </NavLink>
              ) : null}
              {showShopNav ? (
                <NavLink
                  to="/shop"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Shop
                </NavLink>
              ) : null}
              {showContractsNav ? (
                <NavLink
                  to="/contracts"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Contracts
                </NavLink>
              ) : null}
              {showCrmNav ? (
                <NavLink
                  to="/crm"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  CRM
                </NavLink>
              ) : null}
              {showInterconnectsNav ? (
                <NavLink
                  to="/interconnects"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Interconnects
                </NavLink>
              ) : null}
              {showReportingNav ? (
                <NavLink
                  to="/reporting"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Reporting
                </NavLink>
              ) : null}
              {showMaterialsInventoryNav ? (
                <NavLink
                  to="/materials-inventory"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Materials
                </NavLink>
              ) : null}
              {showDatacenterNav ? (
                <NavLink
                  to="/datacenter"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Datacenter
                </NavLink>
              ) : null}
              {showPluginsNav ? (
                <NavLink
                  to="/plugins"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Plugins
                </NavLink>
              ) : null}
              {showAdminUsersNav ? (
                <NavLink
                  to="/admin/users"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Users
                </NavLink>
              ) : null}
              {showMailLogNav ? (
                <NavLink
                  to="/mail-log"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Mail log
                </NavLink>
              ) : null}
              {showPhaseHistoryNav ? (
                <NavLink
                  to="/admin/phase-history"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Phase history
                </NavLink>
              ) : null}
              {showMessagesNav ? (
                <NavLink
                  to="/messages"
                  className={({ isActive }) => `app-nav-button${isActive ? " is-active" : ""}`}
                >
                  Messages
                </NavLink>
              ) : null}
            </nav>
          </div>
          <div className="app-sidebar-footer-actions" onClick={closeMobileNav}>
            {showConstructionCalendar ? <SidebarCalendarButton /> : null}
            {showCommercialMap ? <SidebarGlobeButton /> : null}
            {showSettings ? <SidebarSettingsButton /> : null}
            <SidebarLogoutButton onLogout={logout} />
          </div>
        </aside>
        <div className={`app-main${secondaryRoute ? " plugins-route-main" : ""}`}>
          {impersonationBanner}
          <Outlet />
        </div>
      </div>
      {headerMenu.open && (
        <div
          className="header-context-menu"
          style={{ top: `${headerMenu.y}px`, left: `${headerMenu.x}px` }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={hideColumn}
            disabled={!headerMenu.field || headerMenu.field === "__select__"}
          >
            Hide this column
          </button>
          {hiddenColumns.length > 0 && (
            <>
              <div className="header-context-menu-divider" />
              <div className="header-context-menu-label">Unhide column</div>
              {hiddenColumns.map((col) => (
                <button key={col.field} type="button" onClick={() => unhideColumn(col.field)}>
                  {col.label}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function App() {
  const [rowData, setRowData] = useState([]);
  const [contractFieldsByProjectId, setContractFieldsByProjectId] = useState({});
  const [selectedCount, setSelectedCount] = useState(0);
  const [commercialFiltersActive, setCommercialFiltersActive] = useState(() => {
    const saved = loadBoardGridViewState("commercial");
    return hasActiveFilterModel(saved?.filterModel);
  });
  const [importMessage, setImportMessage] = useState("");
  const [authMode, setAuthMode] = useState("login");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [authMessage, setAuthMessage] = useState(() => readAuthFlashMessage());
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem(AUTH_TOKEN_KEY) || "");
  const [configReady, setConfigReady] = useState(() => !localStorage.getItem(AUTH_TOKEN_KEY));
  const [isAdminUser, setIsAdminUser] = useState(() => readStoredAdminFlag());
  const [searchParams, setSearchParams] = useSearchParams();
  const [inviteEmailLocked, setInviteEmailLocked] = useState(false);
  const inviteLoadRef = useRef({ token: null, locked: false });
  const [matrixUiRev, bumpMatrixUi] = useReducer((x) => x + 1, 0);
  const [headerMenu, setHeaderMenu] = useState({
    open: false,
    x: 0,
    y: 0,
    field: null,
  });
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  /** Which project table is shown: commercial vs expansion (`isCvinExpansion` aligns grid + landing + ACF). */
  const [mainView, setMainView] = useState("commercial");
  const [expandedExpansionGroupKeys, setExpandedExpansionGroupKeys] = useState(() => new Set());
  const [selectedExpansionGroupKeys, setSelectedExpansionGroupKeys] = useState(() => new Set());
  const [expansionSelectionRevision, setExpansionSelectionRevision] = useState(0);
  const expansionGridApiRef = useRef(null);
  const standardGridApiRef = useRef(null);
  /** Notify `ProjectDetailPage` (same or other tabs) after a grid save so change log refetches. */
  const projectPatchBroadcastRef = useRef(null);

  const API_URL = useMemo(() => getApiBase(), []);
  const isLoggedIn = Boolean(authToken);
  const { agGridTheme: boardAgGridTheme } = useBoardTheme();

  useEffect(() => {
    const on = () => bumpMatrixUi();
    window.addEventListener("pb-plugins-changed", on);
    window.addEventListener("pb-user-matrix-settings-changed", on);
    window.addEventListener("pb-impersonation-changed", on);
    return () => {
      window.removeEventListener("pb-plugins-changed", on);
      window.removeEventListener("pb-user-matrix-settings-changed", on);
      window.removeEventListener("pb-impersonation-changed", on);
    };
  }, []);

  let viewerEmail = getViewerEmailFromStorage();
  const userMatrixEnabled = isPluginEnabled(PLUGIN_IDS.USER_MATRIX);
  const userImpersonationEnabled = isPluginEnabled(PLUGIN_IDS.USER_IMPERSONATION);
  const impersonationActive = userImpersonationEnabled && isImpersonating();
  const impersonation = userImpersonationEnabled ? getImpersonation() : null;
  const effectiveAdminUser = hasEffectiveAdminPrivileges();
  const canManagePlugins = canViewerPermission(viewerEmail, "canManagePlugins", true);
  const canManageUserMatrix = canViewerPermission(viewerEmail, "canManageUserMatrix", true);
  const canAccessCommercial = canViewerAccessPanel(viewerEmail, "commercial", userMatrixEnabled);
  const canAccessExpansion = canViewerAccessPanel(viewerEmail, "expansion", userMatrixEnabled);
  const showConstructionCalendar = isLoggedIn && canAccessScheduleCalendar(viewerEmail);
  const showCommercialMap = isLoggedIn && canAccessCommercialMap(viewerEmail);
  const showSettings = isLoggedIn && canAccessSettings(viewerEmail);
  const showContractsNav = canViewContracts(viewerEmail);
  const showCrmNav = canViewCrm(viewerEmail);
  const showInterconnectsNav =
    isPluginEnabled(PLUGIN_IDS.INTERCONNECTS) && canViewInterconnects(viewerEmail);
  const showReportingNav =
    isPluginEnabled(PLUGIN_IDS.REPORTING) && canAccessReporting(viewerEmail);
  const showMaterialsInventoryNav =
    isPluginEnabled(PLUGIN_IDS.MATERIALS_INVENTORY) && canAccessMaterialsInventory(viewerEmail);
  const showDatacenterNav =
    isPluginEnabled(PLUGIN_IDS.DATACENTER_LAYOUT) && canAccessDatacenterLayout(viewerEmail);
  const contractsPluginOn = isPluginEnabled(PLUGIN_IDS.CONTRACTS);
  const showContractGroupCols =
    contractsPluginOn && canViewFullExpansionConstructionContractFields(viewerEmail);
  const showMaterialsCols = canAccessMaterialsInventory(viewerEmail);
  const showAddProjectNav = canAccessCommercial && canCreateCommercialProject(viewerEmail);
  const showAddReportNav = canAccessInspectorAddReport(viewerEmail);
  const showProposalNav = canAccessProposalBuilder(viewerEmail);

  /** matrixUiRev ensures this recomputes after User Matrix localStorage updates. */
  const showMailLogNav =
    userMatrixEnabled &&
    matrixUiRev >= 0 &&
    canViewerPermission(viewerEmail, "canViewMailLog", false);
  const showPhaseHistoryNav = effectiveAdminUser || isViewerAdministrator(viewerEmail);

  useEffect(() => {
    if (isLoggedIn) {
      setInviteEmailLocked(false);
      inviteLoadRef.current = { token: null, locked: false };
      return undefined;
    }
    const token = searchParams.get("invite");
    if (!token) {
      setInviteEmailLocked(false);
      inviteLoadRef.current = { token: null, locked: false };
      return undefined;
    }
    setAuthMode("signup");
    if (inviteLoadRef.current.token === token) {
      setInviteEmailLocked(inviteLoadRef.current.locked);
      return undefined;
    }
    inviteLoadRef.current = { token, locked: false };
    setInviteEmailLocked(false);
    let cancelled = false;
    fetchInvitePreview(token)
      .then((peek) => {
        if (cancelled) return;
        const locked = Boolean(peek?.email);
        if (peek?.email) setEmail(peek.email);
        inviteLoadRef.current = { token, locked };
        setInviteEmailLocked(locked);
      })
      .catch(() => {
        if (!cancelled) {
          inviteLoadRef.current = { token, locked: false };
          setInviteEmailLocked(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, searchParams]);

  useEffect(() => {
    if (isLoggedIn) return;
    const resetToken = searchParams.get("reset");
    if (resetToken) {
      setAuthMode("reset");
      setForgotSent(false);
    }
  }, [isLoggedIn, searchParams]);

  const getAuthHeaders = useCallback(() => {
    const h = { ...buildPluginsHeader() };
    if (authToken) h.Authorization = `Bearer ${authToken}`;
    if (impersonationActive && impersonation?.email) {
      h["X-PB-Impersonate-Email"] = String(impersonation.email).trim().toLowerCase();
    }
    if (impersonationActive && impersonation?.userTypeId) {
      h["X-PB-Impersonate-User-Type-Id"] = String(impersonation.userTypeId).trim();
    }
    return h;
  }, [authToken, impersonationActive, impersonation?.email, impersonation?.userTypeId]);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_EMAIL_KEY);
    writeStoredAdminFlag(false);
    setIsAdminUser(false);
    clearPluginsHydration();
    clearUserMatrixHydration();
    clearEmailTriggersHydration();
    clearEngineeringMaterialsCatalogHydration();
    clearCommercialGridDefaultsHydration();
    clearAllBoardGridViewState();
    setConfigReady(true);
    setAuthToken("");
    setRowData([]);
    setSelectedCount(0);
  }, []);

  useEffect(() => {
    if (!authToken) {
      setConfigReady(true);
      return undefined;
    }
    setConfigReady(false);
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchAppConfig();
        if (cancelled) return;
        hydratePluginsFromServer(cfg.plugins);
        hydrateUserMatrixFromServer(cfg.userMatrix);
        if (cfg.emailTriggers) hydrateEmailTriggersFromServer(cfg.emailTriggers);
        if (cfg.engineeringMaterialsCatalog) {
          hydrateEngineeringMaterialsCatalogFromServer(cfg.engineeringMaterialsCatalog);
        }
        if (cfg.commercialGridDefaults) {
          hydrateCommercialGridDefaultsFromServer(cfg.commercialGridDefaults);
        }
        setSignupEnabled(cfg?.authSettings?.signupEnabled !== false);
        const sess = await postAppConfigSessionInit();
        if (cancelled) return;
        emitMarketplaceRewardFromResponse(sess);
        hydrateUserMatrixFromServer(sess.userMatrix);
        hydrateViewerSessionFromServer(sess.viewer);
        bumpMatrixUi();
        setConfigReady(true);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          const code = err?.response?.data?.code;
          const msg = err?.response?.data?.error;
          if (code === IDLE_PASSWORD_RESET_CODE && msg) {
            setAuthMode("forgot");
            setForgotSent(true);
            setAuthMessage(msg);
          } else if (msg) {
            setAuthMessage(msg);
          }
          logout();
          return;
        }
        console.error(err);
        setConfigReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, logout]);

  const fetchProjects = useCallback(() => {
    return axios
      .get(`${API_URL}/projects`, { headers: getAuthHeaders() })
      .then((res) => setRowData(res.data))
      .catch((err) => {
        if (err.response?.status === 401) {
          const code = err?.response?.data?.code;
          const msg = err?.response?.data?.error;
          if (code === IDLE_PASSWORD_RESET_CODE && msg) {
            setAuthMode("forgot");
            setForgotSent(true);
            setAuthMessage(msg);
          }
          logout();
          return;
        }
        if (!err.response) {
          console.error(
            "Projects request failed (no response). API URL:",
            API_URL,
            err
          );
        } else {
          console.error(err);
        }
      });
  }, [API_URL, getAuthHeaders, logout]);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined;
    const bc = new BroadcastChannel("project-board-sync");
    bc.onmessage = (ev) => {
      if (ev?.data?.type === "project-updated") {
        const patched = ev.data.project;
        if (patched && typeof patched === "object" && patched.id != null) {
          const {
            marketplacePhaseRewards: _mp,
            marketplaceReward: _mr,
            ...row
          } = patched;
          setRowData((prev) => {
            const idx = prev.findIndex((r) => String(r.id) === String(row.id));
            if (idx < 0) return prev;
            const next = prev.slice();
            next[idx] = { ...prev[idx], ...row };
            return next;
          });
        }
        fetchProjects();
      }
    };
    projectPatchBroadcastRef.current = bc;
    return () => {
      bc.close();
      projectPatchBroadcastRef.current = null;
    };
  }, [fetchProjects]);

  /** Expansion rows before contractor visibility filter (contracts batch uses this list). */
  const expansionRowDataAll = useMemo(
    () =>
      rowData
        .filter((r) => isCvinExpansion(r))
        .map((r) => {
          const wo = formatExpansionWorkOrderForDisplay(r);
          if (!wo || wo === String(r.work_order ?? "").trim()) return r;
          return { ...r, work_order: wo };
        }),
    [rowData]
  );

  useEffect(() => {
    if (!contractsPluginOn || !expansionRowDataAll.length) {
      setContractFieldsByProjectId({});
      return undefined;
    }
    let cancelled = false;
    const projectIds = expansionRowDataAll.map((r) => r.id).filter(Boolean);
    fetchBatchProjectContractFields(projectIds, { apiUrl: API_URL, getAuthHeaders })
      .then((result) => {
        if (!cancelled) setContractFieldsByProjectId(result?.byProjectId || {});
      })
      .catch(() => {
        if (!cancelled) setContractFieldsByProjectId({});
      });
    return () => {
      cancelled = true;
    };
  }, [API_URL, contractsPluginOn, expansionRowDataAll, getAuthHeaders]);

  useEffect(() => {
    expansionGridApiRef.current?.refreshCells?.({ force: true });
  }, [contractFieldsByProjectId]);

  const expansionRowData = useMemo(() => {
    // Backend already scopes Contractor lists; wait for contract overlay before
    // dropping expansion rows that only match via Contracts (not ACF).
    const overlayReady =
      !contractsPluginOn ||
      expansionRowDataAll.length === 0 ||
      Object.keys(contractFieldsByProjectId || {}).length > 0;
    if (!overlayReady) return expansionRowDataAll;
    return filterProjectsForContractorViewer(
      expansionRowDataAll,
      viewerEmail,
      contractFieldsByProjectId
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- matrixUiRev bumps contractor name / role changes
  }, [
    expansionRowDataAll,
    viewerEmail,
    contractFieldsByProjectId,
    contractsPluginOn,
    matrixUiRev,
  ]);

  const subWorkOrderCountByBase = useMemo(() => buildSubWorkOrderCountByBase(rowData), [rowData]);

  const standardRowData = useMemo(() => {
    const commercial = rowData.filter(
      (r) =>
        !isCvinExpansion(r) &&
        !isMultisiteParentRow(r) &&
        !commercialWorkOrderGroupPath(r, subWorkOrderCountByBase)
    );
    return filterProjectsForContractorViewer(commercial, viewerEmail, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- matrixUiRev bumps contractor name / role changes
  }, [rowData, subWorkOrderCountByBase, viewerEmail, matrixUiRev]);

  const expansionChildrenByGroupKey = useMemo(() => {
    const m = new Map();
    for (const row of expansionRowData) {
      const key = groupLabelForRow(row, EXPANSION_GROUP_BY);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(row);
    }
    return m;
  }, [expansionRowData]);

  const expansionChildrenMatchingSearch = useCallback(
    (key) => {
      const children = expansionChildrenByGroupKey.get(key) || [];
      const q = normalizeProjectSearchQuery(projectSearchQuery);
      if (!q) return children;
      return children.filter((r) => rowMatchesProjectSearch(r, q));
    },
    [expansionChildrenByGroupKey, projectSearchQuery]
  );

  const countExpansionSelectedProjects = useCallback(
    (groupKeys, api) => {
      const ids = new Set();
      forEachVisibleProjectDataNode(api, (node) => {
        if (node.isSelected?.() && node.data?.id != null) ids.add(node.data.id);
      });
      for (const key of groupKeys) {
        for (const child of expansionChildrenMatchingSearch(key)) {
          if (child.id != null) ids.add(child.id);
        }
      }
      return ids.size;
    },
    [expansionChildrenMatchingSearch]
  );

  const getExpansionSelectedProjectRows = useCallback(
    (groupKeys, api) => {
      const byId = new Map();
      forEachVisibleProjectDataNode(api, (node) => {
        if (node.isSelected?.() && node.data?.id != null) {
          byId.set(node.data.id, node.data);
        }
      });
      for (const key of groupKeys) {
        for (const child of expansionChildrenMatchingSearch(key)) {
          if (child.id != null && !byId.has(child.id)) byId.set(child.id, child);
        }
      }
      return [...byId.values()];
    },
    [expansionChildrenMatchingSearch]
  );

  const syncExpansionGroupChildSelection = useCallback(
    (api, groupKeys, selected) => {
      const keySet = groupKeys instanceof Set ? groupKeys : new Set(groupKeys || []);
      forEachVisibleProjectDataNode(api, (node) => {
        const key = groupLabelForRow(node.data, EXPANSION_GROUP_BY);
        if (keySet.has(key)) node.setSelected(selected);
      });
    },
    []
  );

  const refreshExpansionSelectionUi = useCallback((api) => {
    setExpansionSelectionRevision((n) => n + 1);
    api?.refreshHeader?.();
    api?.refreshCells?.({ force: true, columns: ["__select__"] });
  }, []);

  useEffect(() => {
    const labels = new Set(expansionRowData.map((row) => groupLabelForRow(row, EXPANSION_GROUP_BY)));
    setExpandedExpansionGroupKeys((prev) => {
      const next = new Set();
      for (const key of prev) {
        if (labels.has(key)) next.add(key);
      }
      return next;
    });
    setSelectedExpansionGroupKeys((prev) => {
      const next = new Set();
      for (const key of prev) {
        if (labels.has(key)) next.add(key);
      }
      return next;
    });
  }, [expansionRowData]);

  useEffect(() => {
    if (mainView !== "expansion") {
      setSelectedExpansionGroupKeys(new Set());
      const api = standardGridApiRef.current;
      setSelectedCount(countVisibleSelectedProjects(api));
    }
  }, [mainView]);

  useEffect(() => {
    if (mainView !== "expansion") return;
    const q = normalizeProjectSearchQuery(projectSearchQuery);
    if (!q) return;
    setSelectedExpansionGroupKeys((prev) => {
      if (!prev.size) return prev;
      const allowed = new Set(
        expansionRowData
          .filter((r) => rowMatchesProjectSearch(r, q))
          .map((r) => groupLabelForRow(r, EXPANSION_GROUP_BY))
      );
      const next = new Set();
      for (const key of prev) {
        if (allowed.has(key)) next.add(key);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [mainView, projectSearchQuery, expansionRowData]);

  useEffect(() => {
    if (mainView !== "expansion") return;
    const api = expansionGridApiRef.current;
    setSelectedCount(countExpansionSelectedProjects(selectedExpansionGroupKeys, api));
    refreshExpansionSelectionUi(api);
  }, [
    mainView,
    selectedExpansionGroupKeys,
    countExpansionSelectedProjects,
    refreshExpansionSelectionUi,
    projectSearchQuery,
  ]);

  // When a selected collapsed group is expanded, mark its newly visible children selected.
  useEffect(() => {
    if (mainView !== "expansion" || selectedExpansionGroupKeys.size === 0) return;
    const api = expansionGridApiRef.current;
    syncExpansionGroupChildSelection(api, selectedExpansionGroupKeys, true);
    setSelectedCount(countExpansionSelectedProjects(selectedExpansionGroupKeys, api));
    refreshExpansionSelectionUi(api);
  }, [
    mainView,
    expandedExpansionGroupKeys,
    selectedExpansionGroupKeys,
    syncExpansionGroupChildSelection,
    countExpansionSelectedProjects,
    refreshExpansionSelectionUi,
  ]);

  const expansionGroupKeys = useMemo(
    () => [...expansionChildrenByGroupKey.keys()],
    [expansionChildrenByGroupKey]
  );

  const allExpansionGroupsExpanded = useMemo(
    () =>
      expansionGroupKeys.length > 0 &&
      expansionGroupKeys.every((k) => expandedExpansionGroupKeys.has(k)),
    [expansionGroupKeys, expandedExpansionGroupKeys]
  );

  const toggleAllExpansionGroupsExpanded = useCallback(() => {
    setExpandedExpansionGroupKeys((prev) => {
      const allExpanded =
        expansionGroupKeys.length > 0 &&
        expansionGroupKeys.every((k) => prev.has(k));
      return allExpanded ? new Set() : new Set(expansionGroupKeys);
    });
  }, [expansionGroupKeys]);

  const gridContext = useMemo(
    () => ({
      contractFieldsByProjectId,
      showContractGroupCols,
      expansionSelectionRevision,
      selectedExpansionGroupKeys,
      toggleGroupKey: (key) => {
        setExpandedExpansionGroupKeys((prev) => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        });
      },
      expansionGroupKeys,
      toggleExpansionGroupSelection: (key, selected) => {
        const api = expansionGridApiRef.current;
        setSelectedExpansionGroupKeys((prev) => {
          const next = new Set(prev);
          if (selected) next.add(key);
          else next.delete(key);
          syncExpansionGroupChildSelection(api, [key], selected);
          setSelectedCount(countExpansionSelectedProjects(next, api));
          refreshExpansionSelectionUi(api);
          return next;
        });
      },
      toggleAllExpansionGroups: (selected) => {
        const api = expansionGridApiRef.current;
        if (selected) {
          const visibleKeys = getVisibleExpansionGroupKeys(api);
          const all = new Set(visibleKeys);
          setSelectedExpansionGroupKeys(all);
          syncExpansionGroupChildSelection(api, all, true);
          setSelectedCount(countExpansionSelectedProjects(all, api));
        } else {
          setSelectedExpansionGroupKeys(new Set());
          api?.deselectAll?.();
          setSelectedCount(0);
        }
        refreshExpansionSelectionUi(api);
      },
    }),
    [
      expansionSelectionRevision,
      selectedExpansionGroupKeys,
      expansionGroupKeys,
      contractFieldsByProjectId,
      showContractGroupCols,
      refreshExpansionSelectionUi,
      syncExpansionGroupChildSelection,
      countExpansionSelectedProjects,
    ]
  );

  const expansionDisplayRowData = useMemo(() => {
    const q = normalizeProjectSearchQuery(projectSearchQuery);
    const rows = q
      ? expansionRowData.filter((r) => rowMatchesProjectSearch(r, q))
      : expansionRowData;
    const expandedKeys = q
      ? new Set(rows.map((r) => groupLabelForRow(r, EXPANSION_GROUP_BY)))
      : expandedExpansionGroupKeys;
    return buildGroupedDisplayRows(rows, EXPANSION_GROUP_BY, expandedKeys, {
      contractFieldsByProjectId: contractsPluginOn ? contractFieldsByProjectId : null,
    });
  }, [
    expansionRowData,
    expandedExpansionGroupKeys,
    projectSearchQuery,
    contractsPluginOn,
    contractFieldsByProjectId,
  ]);

  const standardDisplayRowData = useMemo(() => {
    const q = normalizeProjectSearchQuery(projectSearchQuery);
    const rows = q ? standardRowData.filter((r) => rowMatchesProjectSearch(r, q)) : standardRowData;
    return buildGroupedDisplayRows(rows, "", new Set());
  }, [standardRowData, projectSearchQuery]);

  const projectSearchActive = normalizeProjectSearchQuery(projectSearchQuery).length > 0;
  const commercialProjectCount = projectSearchActive
    ? standardDisplayRowData.length
    : standardRowData.length;
  const expansionProjectCount = projectSearchActive
    ? expansionRowData.filter((r) => rowMatchesProjectSearch(r, projectSearchQuery)).length
    : expansionRowData.length;

  const getRowId = useCallback((params) => {
    if (params.data?._rowType === "group") return params.data._groupRowId;
    return String(params.data.id);
  }, []);

  const getExpansionRowClass = useCallback((params) => {
    if (params.data?._rowType === "group" && params.data?._groupBy === EXPANSION_GROUP_BY) {
      return "expansion-group-data-row";
    }
    return undefined;
  }, []);

  const linkRenderer = useCallback(
    (params) => {
      const value = params.value;
      if (!value) return "";
      const pid = params.data?.id;
      if (!pid) return value;
      const groupPath = commercialWorkOrderGroupPath(params.data, subWorkOrderCountByBase);
      const to = groupPath || `/project/${pid}`;
      return (
        <Link className="grid-cell-link" to={to}>
          {value}
        </Link>
      );
    },
    [subWorkOrderCountByBase]
  );

  const canEditPermittingGrid = canEditPermittingTabFields(viewerEmail);
  const canEditEngineeringGrid = canEditEngineeringTabFields(viewerEmail);

  const dataColumnDefsStandard = useMemo(
    () =>
      buildProjectColumnDefs({
        linkRenderer,
        displayPhaseForRow,
        phaseCellClass,
        phaseCellStyle,
        subphaseCellStyle,
        includeMaterialsCols: showMaterialsCols,
        canEditPermittingFields: canEditPermittingGrid,
      }),
    [linkRenderer, showMaterialsCols, canEditPermittingGrid]
  );

  const dataColumnDefsExpansion = useMemo(
    () =>
      buildExpansionColumnDefs({
        linkRenderer,
        displayPhaseForRow,
        phaseCellClass,
        phaseCellStyle,
        subphaseCellStyle,
        includeContractGroupCols: showContractGroupCols,
        includeMaterialsCols: showMaterialsCols,
        canEditPermittingFields: canEditPermittingGrid,
        canEditEngineeringFields: canEditEngineeringGrid,
      }),
    [linkRenderer, showContractGroupCols, showMaterialsCols, canEditPermittingGrid, canEditEngineeringGrid]
  );

  const gridColumnDefsStandard = useMemo(() => {
    const base = [
      {
        colId: "__select__",
        headerName: "",
        width: 52,
        pinned: "left",
        lockPosition: true,
        resizable: false,
        sortable: false,
        filter: false,
        suppressMovable: true,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
      },
      ...dataColumnDefsStandard.map((col) => {
        const colId = col.colId ?? col.field;
        if (!colId || colId === "__select__") return col;
        if (COMMERCIAL_DEFAULT_HIDDEN_COL_IDS.has(String(colId))) {
          return { ...col, hide: true };
        }
        return col;
      }),
    ];
    return forceMaterialsBoardColumnsVisible(
      applyUserMatrixColumnVisibility(base, "commercial", viewerEmail, userMatrixEnabled)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- matrixUiRev bumps when User Matrix localStorage changes (not in closure)
  }, [dataColumnDefsStandard, viewerEmail, userMatrixEnabled, matrixUiRev]);

  const gridColumnDefsExpansion = useMemo(() => {
    const base = [
      {
        colId: "__select__",
        headerName: "",
        width: 52,
        pinned: "left",
        lockPosition: true,
        resizable: false,
        sortable: false,
        filter: false,
        suppressMovable: true,
        checkboxSelection: (params) => params.data?._rowType !== "group",
        headerCheckboxSelection: false,
        headerComponent: ExpansionSelectAllHeader,
        cellRendererSelector: (params) => {
          if (
            params.data?._rowType === "group" &&
            params.data?._groupBy === EXPANSION_GROUP_BY
          ) {
            return { component: ExpansionGroupSelectCellRenderer };
          }
          return undefined;
        },
        cellClassRules: {
          "expansion-group-select-cell": (params) =>
            params.data?._rowType === "group" &&
            params.data?._groupBy === EXPANSION_GROUP_BY,
        },
      },
      {
        colId: "__expansion_group__",
        headerName: "",
        width: 0,
        minWidth: 0,
        maxWidth: 0,
        resizable: false,
        sortable: false,
        filter: false,
        suppressMovable: true,
        colSpan: expansionGroupColSpan,
        cellRendererSelector: (params) => {
          if (
            params.data?._rowType === "group" &&
            params.data?._groupBy === EXPANSION_GROUP_BY
          ) {
            return { component: GroupRowCellRenderer };
          }
          return undefined;
        },
        cellStyle: (params) =>
          params.data?._rowType === "group"
            ? { padding: 0, borderRight: "none" }
            : { display: "none", padding: 0, border: "none" },
      },
      ...dataColumnDefsExpansion,
    ];
    return forceMaterialsBoardColumnsVisible(
      applyUserMatrixColumnVisibility(base, "expansion", viewerEmail, userMatrixEnabled)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- matrixUiRev bumps when User Matrix localStorage changes (not in closure)
  }, [dataColumnDefsExpansion, viewerEmail, userMatrixEnabled, matrixUiRev]);

  const commercialColumnVisibilityKey = useMemo(() => {
    const materialsPart = showMaterialsCols ? "mat" : "nomat";
    if (!userMatrixEnabled) return `all-${materialsPart}`;
    const allowed = getAllowedColumnIdsForViewer(viewerEmail, "commercial");
    if (allowed === null) return `all-${materialsPart}`;
    return `${[...allowed].sort().join(",")}-${materialsPart}`;
  }, [viewerEmail, userMatrixEnabled, matrixUiRev, showMaterialsCols]);

  const expansionColumnVisibilityKey = useMemo(() => {
    const materialsPart = showMaterialsCols ? "mat" : "nomat";
    if (!userMatrixEnabled) return `all-${materialsPart}`;
    const allowed = getAllowedColumnIdsForViewer(viewerEmail, "expansion");
    if (allowed === null) return `all-${materialsPart}`;
    return `${[...allowed].sort().join(",")}-${materialsPart}`;
  }, [viewerEmail, userMatrixEnabled, matrixUiRev, showMaterialsCols]);

  useEffect(() => {
    syncGridColumnVisibility(standardGridApiRef.current, gridColumnDefsStandard);
  }, [gridColumnDefsStandard]);

  useEffect(() => {
    syncGridColumnVisibility(expansionGridApiRef.current, gridColumnDefsExpansion);
  }, [gridColumnDefsExpansion]);

  const refreshHiddenColumns = useCallback((sourceApi) => {
    const api = sourceApi ?? expansionGridApiRef.current ?? standardGridApiRef.current;
    if (!api) return;
    const hidden = (api.getColumns?.() || [])
      .filter((col) => !col.isVisible() && col.getColId() !== "__select__")
      .map((col) => ({
        field: col.getColId(),
        label: col.getColDef().headerName || col.getColId(),
      }));
    setHiddenColumns(hidden);
  }, []);

  const closeHeaderMenu = useCallback(() => {
    setHeaderMenu((prev) => ({ ...prev, open: false }));
  }, []);

  const hideColumn = useCallback(() => {
    const field = headerMenu.field;
    if (!field || field === "__select__") return;
    expansionGridApiRef.current?.setColumnsVisible([field], false);
    standardGridApiRef.current?.setColumnsVisible([field], false);
    refreshHiddenColumns(expansionGridApiRef.current ?? standardGridApiRef.current);
    closeHeaderMenu();
  }, [closeHeaderMenu, headerMenu.field, refreshHiddenColumns]);

  const unhideColumn = useCallback(
    (field) => {
      if (!field || field === "__select__") return;
      const panel = mainView === "expansion" ? "expansion" : "commercial";
      if (userMatrixEnabled) {
        const allowed = getAllowedColumnIdsForViewer(viewerEmail, panel);
        if (allowed !== null && !allowed.has(field)) return;
      }
      expansionGridApiRef.current?.setColumnsVisible([field], true);
      standardGridApiRef.current?.setColumnsVisible([field], true);
      refreshHiddenColumns(expansionGridApiRef.current ?? standardGridApiRef.current);
    },
    [refreshHiddenColumns, mainView, userMatrixEnabled, viewerEmail, matrixUiRev]
  );

  useEffect(() => {
    if (!headerMenu.open) return undefined;
    const onPointerDownCapture = (event) => {
      const t = event.target;
      if (!(t instanceof Element)) return;
      if (t.closest(".header-context-menu")) return;
      if (t.closest(".ag-popup")) return;
      if (t.closest(".ag-dialog")) return;
      closeHeaderMenu();
    };
    document.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => document.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, [headerMenu.open, closeHeaderMenu]);

  const onExpansionGridReady = useCallback(
    (params) => {
      expansionGridApiRef.current = params.api;
      syncGridColumnVisibility(params.api, gridColumnDefsExpansion);
      refreshHiddenColumns(params.api);
      restoreOrApplyExpansionFilters(params.api);
      restoreOrApplyExpansionSort(params.api);
      autoSizeContentFitGridColumns(params.api);
    },
    [refreshHiddenColumns, gridColumnDefsExpansion]
  );

  const onStandardGridReady = useCallback(
    (params) => {
      standardGridApiRef.current = params.api;
      syncGridColumnVisibility(params.api, gridColumnDefsStandard);
      refreshHiddenColumns(params.api);
      restoreOrApplyCommercialFilters(params.api);
      restoreOrApplyCommercialSort(params.api);
      autoSizeContentFitGridColumns(params.api);
    },
    [refreshHiddenColumns, gridColumnDefsStandard]
  );

  const onContentFitColumnsDataRendered = useCallback((params) => {
    autoSizeContentFitGridColumns(params.api);
  }, []);

  useEffect(() => {
    autoSizeContentFitGridColumns(standardGridApiRef.current);
    autoSizeContentFitGridColumns(expansionGridApiRef.current);
  }, [standardDisplayRowData, expansionDisplayRowData]);

  const resetCommercialFilters = useCallback(() => {
    clearBoardGridFilterState("commercial");
    applyCommercialGridDefaultFilters(standardGridApiRef.current);
  }, []);

  useEffect(() => {
    const onDefaultsChanged = () => {
      // Role / admin defaults changed — drop saved filters so the new defaults apply.
      clearBoardGridFilterState("commercial");
      applyCommercialGridDefaultFilters(standardGridApiRef.current);
    };
    const onMatrixChanged = () => {
      clearAllBoardGridViewState();
      applyCommercialGridDefaultFilters(standardGridApiRef.current);
    };
    window.addEventListener("pb-commercial-grid-defaults-changed", onDefaultsChanged);
    window.addEventListener("pb-user-matrix-settings-changed", onMatrixChanged);
    return () => {
      window.removeEventListener("pb-commercial-grid-defaults-changed", onDefaultsChanged);
      window.removeEventListener("pb-user-matrix-settings-changed", onMatrixChanged);
    };
  }, []);

  useEffect(() => {
    // Matrix / impersonation refresh: keep session filters when present; otherwise apply defaults.
    const api = standardGridApiRef.current;
    if (!api) return;
    const saved = loadBoardGridViewState("commercial");
    if (saved?.filtersInitialized) {
      api.setFilterModel(saved.filterModel || null);
      return;
    }
    applyCommercialGridDefaultFilters(api);
  }, [matrixUiRev]);

  const onColumnVisible = useCallback(
    (event) => {
      const col = event.column;
      const api = event.api;
      if (!col || !api) {
        refreshHiddenColumns();
        return;
      }
      const colId = col.getColId();
      const visible = col.isVisible();
      if (userMatrixEnabled && visible && colId !== "__select__") {
        const panel = api === expansionGridApiRef.current ? "expansion" : "commercial";
        const allowed = getAllowedColumnIdsForViewer(viewerEmail, panel);
        if (allowed !== null && !allowed.has(colId)) {
          api.setColumnsVisible([colId], false);
          refreshHiddenColumns(api);
          return;
        }
      }
      const other =
        api === expansionGridApiRef.current ? standardGridApiRef.current : expansionGridApiRef.current;
      if (other && colId !== "__select__") {
        const oc = other.getColumn(colId);
        if (oc && oc.isVisible() !== visible) {
          other.setColumnsVisible([colId], visible);
        }
      }
      refreshHiddenColumns(api);
      if (visible && colId === "active_subphase") {
        autoSizeContentFitGridColumns(api);
      }
    },
    [refreshHiddenColumns, userMatrixEnabled, viewerEmail, matrixUiRev]
  );

  const onAnySelectionChanged = useCallback(() => {
    if (mainView === "expansion") {
      const api = expansionGridApiRef.current;
      // If the user unchecked a visible child under a selected group, drop that group key.
      if (selectedExpansionGroupKeys.size > 0) {
        const stillFullySelected = new Set();
        for (const key of selectedExpansionGroupKeys) {
          let hasVisible = false;
          let allSelected = true;
          forEachVisibleProjectDataNode(api, (node) => {
            if (groupLabelForRow(node.data, EXPANSION_GROUP_BY) !== key) return;
            hasVisible = true;
            if (!node.isSelected?.()) allSelected = false;
          });
          // Collapsed groups have no visible children — keep the group selection.
          if (!hasVisible || allSelected) stillFullySelected.add(key);
        }
        if (stillFullySelected.size !== selectedExpansionGroupKeys.size) {
          setSelectedExpansionGroupKeys(stillFullySelected);
          setSelectedCount(countExpansionSelectedProjects(stillFullySelected, api));
          refreshExpansionSelectionUi(api);
          return;
        }
      }
      setSelectedCount(countExpansionSelectedProjects(selectedExpansionGroupKeys, api));
      refreshExpansionSelectionUi(api);
      return;
    }
    const api = standardGridApiRef.current;
    setSelectedCount(countVisibleSelectedProjects(api));
  }, [
    mainView,
    selectedExpansionGroupKeys,
    countExpansionSelectedProjects,
    refreshExpansionSelectionUi,
  ]);

  const onCommercialFilterChanged = useCallback(
    (event) => {
      const api = event?.api || standardGridApiRef.current;
      persistGridFilterModel("commercial", api);
      setCommercialFiltersActive(hasActiveFilterModel(api?.getFilterModel?.()));
      onAnySelectionChanged();
    },
    [onAnySelectionChanged]
  );

  const onExpansionFilterChanged = useCallback(
    (event) => {
      persistGridFilterModel("expansion", event?.api || expansionGridApiRef.current);
      onAnySelectionChanged();
    },
    [onAnySelectionChanged]
  );

  const onCommercialSortChanged = useCallback((event) => {
    persistGridSortState("commercial", event?.api || standardGridApiRef.current);
  }, []);

  const onExpansionSortChanged = useCallback((event) => {
    persistGridSortState("expansion", event?.api || expansionGridApiRef.current);
  }, []);

  useEffect(() => {
    onAnySelectionChanged();
  }, [projectSearchQuery, onAnySelectionChanged]);

  /** Keep synthetic expansion group rows directly above their children after AG sort. */
  const postSortExpansionRows = useCallback((params) => {
    const nodes = Array.isArray(params?.nodes) ? params.nodes : [];
    if (nodes.length === 0) return;

    const groupNodeByKey = new Map();
    const childNodesByKey = new Map();
    for (const node of nodes) {
      const d = node?.data;
      if (!d) continue;
      if (d._rowType === "group") {
        groupNodeByKey.set(String(d._groupKey || ""), node);
        continue;
      }
      const key = groupLabelForRow(d, EXPANSION_GROUP_BY);
      if (!childNodesByKey.has(key)) childNodesByKey.set(key, []);
      childNodesByKey.get(key).push(node);
    }

    const sortState =
      params.api
        ?.getColumnState?.()
        ?.filter((c) => c.sort != null)
        ?.sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0)) ?? [];
    const primarySort = sortState[0];
    const dir = primarySort?.sort === "desc" ? -1 : 1;

    const sortChildrenByWorkOrder = shouldSortExpansionChildrenByWorkOrder(primarySort);
    const childWoDir =
      primarySort?.colId === "work_order" && primarySort?.sort === "desc" ? -1 : 1;

    const groupKeys = [...new Set([...groupNodeByKey.keys(), ...childNodesByKey.keys()])];
    groupKeys.sort((ka, kb) => {
      const ga = groupNodeByKey.get(ka)?.data;
      const gb = groupNodeByKey.get(kb)?.data;
      const colId = primarySort?.colId;

      const valueForKey = (key, groupData) => {
        if (colId === "exp_segment") {
          const seg = groupData?._groupSegment ?? "";
          const n = segmentSortValue(seg);
          return Number.isFinite(n) ? n : seg;
        }
        if (colId === "work_order" || colId === "work_order_base") {
          const n = parseFloat(String(groupData?._groupWoBase ?? key));
          return Number.isFinite(n) ? n : String(groupData?._groupWoBase ?? key);
        }
        const kids = childNodesByKey.get(key) || [];
        const first = kids[0]?.data;
        if (colId === "exp_segment") {
          const sub = readSubsegmentSummaryFromAcf(first?.acf_groups_json);
          return sub?.segment ?? "";
        }
        if (colId === "work_order") {
          return first ? formatExpansionWorkOrderForDisplay(first) : key;
        }
        return key;
      };

      const va = valueForKey(ka, ga);
      const vb = valueForKey(kb, gb);
      if (typeof va === "number" && typeof vb === "number" && va !== vb) return (va - vb) * dir;
      return (
        String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" }) * dir
      );
    });

    const rebuilt = [];
    for (const key of groupKeys) {
      const groupNode = groupNodeByKey.get(key);
      if (groupNode) rebuilt.push(groupNode);
      const kids = [...(childNodesByKey.get(key) || [])];
      if (sortChildrenByWorkOrder) {
        kids.sort(
          (a, b) =>
            compareExpansionSubsegmentRows(a?.data, b?.data) * childWoDir
        );
      }
      for (const n of kids) rebuilt.push(n);
    }
    params.nodes.length = 0;
    params.nodes.push(...rebuilt);
  }, []);

  const getSelectedProjectRowsForActiveGrid = useCallback(() => {
    if (mainView === "expansion") {
      return getExpansionSelectedProjectRows(
        selectedExpansionGroupKeys,
        expansionGridApiRef.current
      );
    }
    return getVisibleSelectedProjectRows(standardGridApiRef.current);
  }, [mainView, selectedExpansionGroupKeys, getExpansionSelectedProjectRows]);

  const clearGridSelection = useCallback(() => {
    expansionGridApiRef.current?.deselectAll?.();
    standardGridApiRef.current?.deselectAll?.();
    setSelectedExpansionGroupKeys(new Set());
    setSelectedCount(0);
    refreshExpansionSelectionUi(expansionGridApiRef.current);
  }, [refreshExpansionSelectionUi]);

  const exportSelected = useCallback(async () => {
    if (mainView === "expansion") {
      const api = expansionGridApiRef.current;
      const selected = getSelectedProjectRowsForActiveGrid();
      if (selected.length === 0) return;
      const cols = getExportColumnDefs(api, dataColumnDefsExpansion);
      const exportRows = buildExpansionExportRows(
        selected,
        selectedExpansionGroupKeys,
        expansionDisplayRowData
      );
      exportProjectRowsToCsv(exportRows, cols, gridContext, "expansion-projects.csv");
      return;
    }
    const api = standardGridApiRef.current;
    const selected = getSelectedProjectRowsForActiveGrid();
    if (selected.length === 0) return;
    const cols = getExportColumnDefs(api, dataColumnDefsStandard);
    exportProjectRowsToCsv(selected, cols, gridContext, "commercial-projects.csv");
  }, [
    mainView,
    getSelectedProjectRowsForActiveGrid,
    selectedExpansionGroupKeys,
    expansionDisplayRowData,
    dataColumnDefsStandard,
    dataColumnDefsExpansion,
    gridContext,
  ]);

  const deleteSelected = useCallback(async () => {
    const selected = getSelectedProjectRowsForActiveGrid();
    if (selected.length === 0) return;
    const ids = selected.map((r) => r.id);
    const label =
      selected.length === 1
        ? `project ${selected[0].work_order || selected[0].name || ids[0]}`
        : `${selected.length} projects`;
    if (
      !window.confirm(
        `Archive and remove ${label} from the board? Data is kept in the archive database and can be restored by an administrator.`
      )
    ) {
      return;
    }
    try {
      const res = await axios.post(
        `${API_URL}/projects/archive-delete`,
        { ids },
        { headers: getAuthHeaders() }
      );
      const n = res.data?.archived ?? 0;
      const notFound = res.data?.notFound?.length ?? 0;
      let msg = `Archived ${n} project${n === 1 ? "" : "s"}.`;
      if (notFound > 0) msg += ` ${notFound} not found.`;
      setImportMessage(msg);
      clearGridSelection();
      await fetchProjects();
    } catch (err) {
      const apiMsg = err.response?.data?.error;
      setImportMessage(
        typeof apiMsg === "string" ? apiMsg : err.message || "Could not archive projects."
      );
    }
  }, [API_URL, clearGridSelection, fetchProjects, getAuthHeaders, getSelectedProjectRowsForActiveGrid]);

  const onProjectCellValueChanged = useCallback(
    async (event) => {
      const row = event.data;
      if (!row || row._rowType === "group") return;
      const field = event.colDef?.field;
      const colId = event.colDef?.colId || field;
      if ((!field && !colId) || field === "__select__" || colId === "__select__") return;
      const id = row.id;
      if (!id) return;
      const newValue = event.newValue;

      const permittingKeyMap = {
        permit_status: "permit_status",
        revision_requested_date: "revision_requested_date",
        permit_follow_up: "permit_follow_up",
        exp_permit_status: "permit_status",
        exp_revision_requested_date: "revision_requested_date",
        exp_permit_follow_up: "permit_follow_up",
      };
      const permittingKey = permittingKeyMap[colId] || permittingKeyMap[field];
      if (permittingKey && PERMITTING_TRACKING_FIELD_KEYS.includes(permittingKey)) {
        if (!canEditPermittingTabFields(getViewerEmailFromStorage())) {
          if (event.node?.setDataValue) {
            event.node.setDataValue(colId || field, event.oldValue, false);
          }
          setImportMessage("You do not have permission to edit permitting fields.");
          return;
        }
        const body = buildPermittingTrackingFieldPatch(row, permittingKey, newValue);
        if (!body) return;
        try {
          await axios.patch(`${API_URL}/projects/${id}`, body, { headers: getAuthHeaders() });
          await fetchProjects();
          projectPatchBroadcastRef.current?.postMessage({
            type: "project-updated",
            projectId: id,
          });
        } catch (err) {
          const apiErr = err.response?.data?.error;
          if (event.node?.setDataValue) {
            event.node.setDataValue(colId || field, event.oldValue, false);
          }
          setImportMessage(
            typeof apiErr === "string"
              ? `Could not save ${colId || field}: ${apiErr}`
              : `Could not save ${colId || field}.`
          );
        }
        return;
      }

      const footageKeyMap = {
        exp_construction_footage: "construction_footage",
        exp_override_footage: "override_footage",
        construction_footage: "construction_footage",
        override_footage: "override_footage",
      };
      const footageKey = footageKeyMap[colId] || footageKeyMap[field];
      if (footageKey && EXPANSION_FOOTAGE_GRID_FIELD_KEYS.includes(footageKey)) {
        if (!canEditEngineeringTabFields(getViewerEmailFromStorage())) {
          if (event.node?.setDataValue) {
            event.node.setDataValue(colId || field, event.oldValue, false);
          }
          setImportMessage("You do not have permission to edit footage fields.");
          return;
        }
        const body = buildExpansionFootageFieldPatch(row, footageKey, newValue);
        if (!body) {
          if (event.node?.setDataValue) {
            event.node.setDataValue(colId || field, event.oldValue, false);
          }
          setImportMessage("Enter a valid footage number.");
          return;
        }
        try {
          await axios.patch(`${API_URL}/projects/${id}`, body, { headers: getAuthHeaders() });
          await fetchProjects();
          projectPatchBroadcastRef.current?.postMessage({
            type: "project-updated",
            projectId: id,
          });
        } catch (err) {
          const apiErr = err.response?.data?.error;
          if (event.node?.setDataValue) {
            event.node.setDataValue(colId || field, event.oldValue, false);
          }
          setImportMessage(
            typeof apiErr === "string"
              ? `Could not save ${colId || field}: ${apiErr}`
              : `Could not save ${colId || field}.`
          );
        }
        return;
      }

      if (!field) return;
      try {
        await axios.patch(
          `${API_URL}/projects/${id}`,
          { [field]: newValue, _source: "grid" },
          { headers: getAuthHeaders() }
        );
        await fetchProjects();
        projectPatchBroadcastRef.current?.postMessage({
          type: "project-updated",
          projectId: id,
        });
      } catch (err) {
        const apiErr = err.response?.data?.error;
        if (event.node?.setDataValue) {
          event.node.setDataValue(field, event.oldValue, false);
        }
        setImportMessage(
          typeof apiErr === "string" ? `Could not save ${field}: ${apiErr}` : `Could not save ${field}.`
        );
      }
    },
    [API_URL, fetchProjects, getAuthHeaders]
  );

  const handleAuthSubmit = useCallback(async (event) => {
    event.preventDefault();
    setAuthMessage("");
    const normalizedEmail = email.trim().toLowerCase();
    const domain = normalizedEmail.split("@")[1] || "";
    if (!normalizedEmail || !password) {
      setAuthMessage("Email and password are required.");
      return;
    }
    if (authMode === "signup" && !ALLOWED_SIGNUP_DOMAINS.includes(domain)) {
      setAuthMessage("Signup restricted to cvin.com, vastnetworks.com, or calore.net email domains.");
      return;
    }
    if (authMode === "signup" && !signupEnabled && !searchParams.get("invite")) {
      setAuthMessage("Signup is disabled. Contact an administrator.");
      return;
    }

    setIsAuthenticating(true);
    try {
      const endpoint = authMode === "signup" ? "/auth/signup" : "/auth/login";
      const inviteToken = searchParams.get("invite") || undefined;
      const res = await axios.post(`${API_URL}${endpoint}`, {
        email: normalizedEmail,
        password,
        appOrigin: window.location.origin,
        ...(authMode === "signup" ? { inviteToken } : {}),
      });
      const token = res.data?.token;
      const userEmail = res.data?.user?.email || normalizedEmail;
      if (!token) {
        setAuthMessage("Auth response missing token.");
        return;
      }
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem(AUTH_USER_EMAIL_KEY, userEmail);
      const admin = Boolean(res.data?.user?.admin);
      writeStoredAdminFlag(admin);
      setIsAdminUser(admin);
      setAuthToken(token);
      setPassword("");
      if (searchParams.get("invite")) {
        setSearchParams({}, { replace: true });
      }
    } catch (error) {
      const data = error?.response?.data;
      const apiErr = data?.error;
      const status = error?.response?.status;
      const code = data?.code;
      if (!error.response) {
        setAuthMessage(
          `Cannot reach the API at ${API_URL}. For local dev, run the backend on port 4000 or set REACT_APP_API_URL in frontend/.env.`
        );
      } else if (code === IDLE_PASSWORD_RESET_CODE) {
        setPassword("");
        setAuthMode("forgot");
        setForgotSent(Boolean(data?.resetEmailSent));
        setAuthMessage(
          apiErr ||
            "This account has been inactive for 7 days. Check your email for a password reset link to confirm ownership, then choose a new password."
        );
      } else if (status === 429) {
        setAuthMessage(apiErr || "Too many attempts. Please wait and try again.");
      } else {
        setAuthMessage(
          apiErr ||
            (status === 404
              ? "Auth API not available. Deploy the latest backend with /auth/signup and restart it."
              : null) ||
            error?.message ||
            "Authentication failed."
        );
      }
    } finally {
      setIsAuthenticating(false);
    }
  }, [API_URL, authMode, email, password, searchParams, setSearchParams, signupEnabled]);

  const handleForgotSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setAuthMessage("");
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) {
        setAuthMessage("Email is required.");
        return;
      }
      setIsAuthenticating(true);
      try {
        await postForgotPassword({
          email: normalizedEmail,
          appOrigin: window.location.origin,
        });
        setForgotSent(true);
        setAuthMessage(
          "If an account exists for that email, a password reset link has been sent."
        );
      } catch (error) {
        const apiErr = error?.response?.data?.error;
        const status = error?.response?.status;
        setAuthMessage(
          apiErr ||
            (status === 429
              ? "Too many reset requests. Please wait and try again."
              : null) ||
            error?.message ||
            "Could not send reset email."
        );
      } finally {
        setIsAuthenticating(false);
      }
    },
    [email]
  );

  const handleResetSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setAuthMessage("");
      const token = searchParams.get("reset");
      if (!token) {
        setAuthMessage("Missing reset token. Use the link from your email.");
        return;
      }
      if (!password || password.length < 8) {
        setAuthMessage("Password must be at least 8 characters.");
        return;
      }
      if (password !== confirmPassword) {
        setAuthMessage("Passwords do not match.");
        return;
      }
      setIsAuthenticating(true);
      try {
        await postResetPassword({ token, newPassword: password });
        setPassword("");
        setConfirmPassword("");
        setAuthMode("login");
        setSearchParams({}, { replace: true });
        setAuthMessage("Password updated. You can log in with your new password.");
      } catch (error) {
        const apiErr = error?.response?.data?.error;
        const status = error?.response?.status;
        setAuthMessage(
          apiErr ||
            (status === 429
              ? "Too many reset attempts. Please wait and try again."
              : null) ||
            error?.message ||
            "Could not reset password."
        );
      } finally {
        setIsAuthenticating(false);
      }
    },
    [confirmPassword, password, searchParams, setSearchParams]
  );

  useEffect(() => {
    const hasInvite = Boolean(searchParams.get("invite"));
    if (!signupEnabled && authMode === "signup" && !hasInvite) {
      setAuthMode("login");
    }
  }, [signupEnabled, authMode, searchParams]);

  const onHeaderContextMenu = useCallback((event) => {
    event.event?.preventDefault();
    event.event?.stopPropagation();
    refreshHiddenColumns(event.api);
    setHeaderMenu({
      open: true,
      x: event.event?.clientX ?? 0,
      y: event.event?.clientY ?? 0,
      field: event.column?.getColId() || null,
    });
  }, [refreshHiddenColumns]);

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchProjects();
  }, [fetchProjects, isLoggedIn]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
    return () => cancelAnimationFrame(id);
  }, [mainView]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const refreshGrids = () => {
      requestAnimationFrame(() => {
        standardGridApiRef.current?.sizeColumnsToFit?.();
        expansionGridApiRef.current?.sizeColumnsToFit?.();
      });
    };
    window.addEventListener("resize", refreshGrids);
    window.addEventListener("orientationchange", refreshGrids);
    return () => {
      window.removeEventListener("resize", refreshGrids);
      window.removeEventListener("orientationchange", refreshGrids);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (mainView === "commercial" && !canAccessCommercial && canAccessExpansion) {
      setMainView("expansion");
    } else if (mainView === "expansion" && !canAccessExpansion && canAccessCommercial) {
      setMainView("commercial");
    }
  }, [mainView, canAccessCommercial, canAccessExpansion]);

  if (!isLoggedIn) {
    const resetToken = searchParams.get("reset");
    const inviteToken = searchParams.get("invite");
    const authTitle =
      authMode === "signup"
        ? "Create your account"
        : authMode === "forgot"
          ? "Reset password"
          : authMode === "reset"
            ? "Choose a new password"
            : "Project Board Login";
    const authSubtitle =
      authMode === "signup"
        ? inviteToken
          ? "Complete signup with the email from your invitation."
          : "Sign up allowed for cvin.com, vastnetworks.com, and calore.net (cvin.com and vastnetworks.com share the same account)."
        : authMode === "forgot"
          ? "Enter your email and we will send a reset link if an account exists."
          : authMode === "reset"
            ? "Enter a new password for your account."
            : signupEnabled
              ? "Log in with your organization credentials. cvin.com and vastnetworks.com use the same account."
              : "Log in with your organization credentials. Signup is currently disabled by an administrator.";

    return (
      <div className="auth-shell">
        <form
          className="auth-card"
          onSubmit={
            authMode === "forgot"
              ? handleForgotSubmit
              : authMode === "reset"
                ? handleResetSubmit
                : handleAuthSubmit
          }
        >
          <h2>{authTitle}</h2>
          <p className="auth-subtitle">{authSubtitle}</p>
          {authMode !== "reset" ? (
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                readOnly={authMode === "signup" && inviteEmailLocked}
              />
            </label>
          ) : null}
          {authMode === "forgot" ? null : (
            <label className="auth-password-label">
              {authMode === "reset" ? "New password" : "Password"}
              <span className="auth-password-row">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={authMode === "login" ? "current-password" : "new-password"}
                  required
                />
                <button
                  type="button"
                  className="auth-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-pressed={showPassword}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </span>
            </label>
          )}
          {authMode === "reset" ? (
            <label className="auth-password-label">
              Confirm password
              <span className="auth-password-row">
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </span>
            </label>
          ) : null}
          <button type="submit" disabled={isAuthenticating || (authMode === "forgot" && forgotSent)}>
            {isAuthenticating
              ? "Please wait..."
              : authMode === "signup"
                ? "Create account"
                : authMode === "forgot"
                  ? forgotSent
                    ? "Email sent"
                    : "Send reset link"
                  : authMode === "reset"
                    ? "Update password"
                    : "Log in"}
          </button>
          {authMessage && <div className="auth-message">{authMessage}</div>}
          {authMode === "login" ? (
            <div className="auth-toggle-row">
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setAuthMessage("");
                  setForgotSent(false);
                  setAuthMode("forgot");
                }}
              >
                Forgot password?
              </button>
            </div>
          ) : null}
          {authMode === "forgot" || authMode === "reset" ? (
            <div className="auth-toggle-row">
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setAuthMessage("");
                  setForgotSent(false);
                  setPassword("");
                  setConfirmPassword("");
                  setAuthMode("login");
                  if (resetToken) setSearchParams({}, { replace: true });
                }}
              >
                Back to log in
              </button>
            </div>
          ) : null}
          {signupEnabled || inviteToken ? (
            authMode === "login" || authMode === "signup" ? (
              <div className="auth-toggle-row">
                {authMode === "signup" ? "Already have an account?" : "Need an account?"}{" "}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setAuthMessage("");
                    setAuthMode((prev) => (prev === "signup" ? "login" : "signup"));
                  }}
                >
                  {authMode === "signup" ? "Log in" : "Sign up"}
                </button>
              </div>
            ) : null
          ) : null}
        </form>
      </div>
    );
  }

  if (!configReady) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h2>Loading settings...</h2>
          <p className="auth-subtitle">Syncing your role, plugins, and permissions.</p>
        </div>
      </div>
    );
  }

  const impersonationBanner = impersonationActive ? (
    <div className="impersonation-banner" role="status">
      <span>
        Impersonating role: <strong>{impersonation?.roleName || impersonation?.userTypeId}</strong>
        {impersonation?.email ? ` (${impersonation.email})` : ""}
      </span>
      <button type="button" onClick={() => stopImpersonation()}>
        Exit
      </button>
    </div>
  ) : null;

  return (
    <>
      <Routes>
      <Route
        path="/"
        element={(
          <DashboardLayout
            mainView={mainView}
            setMainView={setMainView}
            headerMenu={headerMenu}
            hideColumn={hideColumn}
            unhideColumn={unhideColumn}
            hiddenColumns={hiddenColumns}
            logout={logout}
            showMailLogNav={showMailLogNav}
            showPhaseHistoryNav={showPhaseHistoryNav}
            showMessagesNav={canAccessMessages(viewerEmail)}
            showPluginsNav={effectiveAdminUser && canManagePlugins}
            showAdminUsersNav={effectiveAdminUser && canManageUserMatrix}
            canAccessCommercial={canAccessCommercial}
            canAccessExpansion={canAccessExpansion}
            showConstructionCalendar={showConstructionCalendar}
            showCommercialMap={showCommercialMap}
            showSettings={showSettings}
            showAddProjectNav={showAddProjectNav}
            showAddReportNav={showAddReportNav}
            showProposalNav={showProposalNav}
            showShopNav={canAccessShop(viewerEmail)}
            showContractsNav={showContractsNav}
            showCrmNav={showCrmNav}
            showInterconnectsNav={showInterconnectsNav}
            showReportingNav={showReportingNav}
            showMaterialsInventoryNav={showMaterialsInventoryNav}
            showDatacenterNav={showDatacenterNav}
            impersonationBanner={impersonationBanner}
          />
        )}
      >
        <Route
          path="project/:id"
          element={<ProjectDetailPage apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
        />
        <Route
          path="work-order/:base/add"
          element={<AddCommercialChildGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
        />
        <Route
          path="work-order/:base"
          element={<ParentWorkOrderGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
        />
        <Route
          path="expansion/segment/:base/add"
          element={<AddExpansionSubsegmentGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
        />
        <Route
          path="expansion/segment/:base"
          element={<ExpansionSegmentGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
        />
        <Route
          index
          element={(
            <>
              <style>{`
                .grid-section-heading {
                  display: grid;
                  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
                  align-items: center;
                  gap: 12px;
                  flex-shrink: 0;
                  margin-bottom: 6px;
                }
                .grid-section-title { justify-self: start; min-width: 0; }
                .grid-section-actions { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; justify-self: end; min-width: 0; }
                .group-row-segment-link {
                  color: inherit;
                  font-weight: inherit;
                  text-decoration: none;
                }
                .group-row-segment-link:hover {
                  text-decoration: underline;
                  color: var(--pb-content-link, #1d4ed8);
                }
                .ag-cell.expansion-group-select-cell {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
                .ag-cell.expansion-group-rollup-cell {
                  background: #f1f5f9;
                  font-weight: 600;
                  color: #0f172a;
                }
                .ag-row.expansion-group-data-row .ag-cell {
                  background: #f1f5f9;
                }
                .grid-section-delete-link {
                  font: inherit; font-size: 13px; font-weight: 600; color: #b91c1c;
                  text-decoration: underline; text-underline-offset: 2px; cursor: pointer;
                  background: none; border: 0; padding: 0; white-space: nowrap;
                }
                .grid-section-delete-link:hover { color: #991b1b; }
                .grid-section-clear-link {
                  font: inherit; font-size: 13px; font-weight: 600; color: #475569;
                  text-decoration: underline; text-underline-offset: 2px; cursor: pointer;
                  background: none; border: 0; padding: 0; white-space: nowrap;
                }
                .grid-section-clear-link:hover { color: #0f172a; }
                .grid-section-clear-link:disabled {
                  color: #94a3b8;
                  cursor: not-allowed;
                  text-decoration: none;
                }
              `}</style>
              {importMessage ? (
                <div className="app-status-message" role="status">
                  {importMessage}
                </div>
              ) : null}
              <div className="app-grid-panels">
                  {!canAccessCommercial && !canAccessExpansion ? (
                    <div className="app-status-message" role="status">
                      Your role currently has no board visibility. Ask an administrator to enable Commercial and/or Expansion access for your role.
                    </div>
                  ) : null}
                  <section
                    className={`app-grid-panel${mainView === "commercial" ? " is-active" : ""}`}
                    aria-hidden={mainView !== "commercial"}
                    aria-label="Commercial projects"
                  >
                    <div className="grid-section-heading">
                      <h2 className="grid-section-title">
                        Commercial
                        <span className="grid-section-meta">
                          {projectSearchActive
                            ? ` (${commercialProjectCount} of ${standardRowData.length} projects)`
                            : ` (${standardRowData.length} projects)`}
                        </span>
                      </h2>
                      <div className="grid-section-search">
                        <BoardProjectSearch
                          value={projectSearchQuery}
                          onChange={setProjectSearchQuery}
                        />
                      </div>
                      {mainView === "commercial" &&
                      (commercialFiltersActive || selectedCount > 0) ? (
                        <div className="grid-section-actions">
                          {commercialFiltersActive ? (
                            <button
                              type="button"
                              className="grid-section-clear-link"
                              onClick={resetCommercialFilters}
                            >
                              Reset filters
                            </button>
                          ) : null}
                          {selectedCount > 0 ? (
                            <>
                              <button
                                type="button"
                                className="grid-section-export-link"
                                onClick={exportSelected}
                              >
                                Export selected ({selectedCount})
                              </button>
                              {effectiveAdminUser ? (
                                <button
                                  type="button"
                                  className="grid-section-delete-link"
                                  onClick={deleteSelected}
                                >
                                  Delete ({selectedCount})
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="grid-section-clear-link"
                                onClick={clearGridSelection}
                              >
                                Clear selection
                              </button>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="grid-wrap">
                      <AgGridReact
                        key={`commercial-cols-${commercialColumnVisibilityKey}`}
                        theme={boardAgGridTheme}
                        rowData={standardDisplayRowData}
                        columnDefs={gridColumnDefsStandard}
                        getRowId={getRowId}
                        context={gridContext}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        defaultColDef={{
                          isCellEditable: (p) => p.data?._rowType !== "group",
                        }}
                        onGridReady={onStandardGridReady}
                        onFirstDataRendered={onContentFitColumnsDataRendered}
                        onSelectionChanged={onAnySelectionChanged}
                        onFilterChanged={onCommercialFilterChanged}
                        onSortChanged={onCommercialSortChanged}
                        onColumnVisible={onColumnVisible}
                        onHeaderContextMenu={onHeaderContextMenu}
                        onCellValueChanged={onProjectCellValueChanged}
                      />
                    </div>
                    {mainView === "commercial" &&
                    canAccessCommercial &&
                    isPluginEnabled(PLUGIN_IDS.AI_CHAT_PORTFOLIO) &&
                    canUsePortfolioAi(viewerEmail) ? (
                      <PortfolioAiChatPanel />
                    ) : null}
                  </section>
                  <section
                    className={`app-grid-panel${mainView === "expansion" ? " is-active" : ""}`}
                    aria-hidden={mainView !== "expansion"}
                    aria-label="Expansion projects"
                  >
                    <div className="grid-section-heading">
                      <h2 className="grid-section-title">
                        Expansion
                        <span className="grid-section-meta">
                          {projectSearchActive
                            ? ` (${expansionProjectCount} of ${expansionRowData.length} projects, grouped by parent work order)`
                            : ` (${expansionRowData.length} projects, grouped by parent work order)`}
                        </span>
                      </h2>
                      <div className="grid-section-search">
                        <BoardProjectSearch
                          value={projectSearchQuery}
                          onChange={setProjectSearchQuery}
                        />
                      </div>
                      {mainView === "expansion" ? (
                        <div className="grid-section-actions">
                          {!projectSearchActive ? (
                            <button
                              type="button"
                              className="grid-section-clear-link"
                              onClick={toggleAllExpansionGroupsExpanded}
                              disabled={expansionGroupKeys.length === 0}
                            >
                              {allExpansionGroupsExpanded ? "Collapse all" : "Expand all"}
                            </button>
                          ) : null}
                          {selectedCount > 0 ? (
                            <>
                              <button
                                type="button"
                                className="grid-section-export-link"
                                onClick={exportSelected}
                              >
                                Export selected ({selectedCount})
                              </button>
                              {effectiveAdminUser ? (
                                <button
                                  type="button"
                                  className="grid-section-delete-link"
                                  onClick={deleteSelected}
                                >
                                  Delete ({selectedCount})
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="grid-section-clear-link"
                                onClick={clearGridSelection}
                              >
                                Clear selection
                              </button>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="grid-wrap">
                      <AgGridReact
                        key={`expansion-cols-${expansionColumnVisibilityKey}`}
                        theme={boardAgGridTheme}
                        rowData={expansionDisplayRowData}
                        columnDefs={gridColumnDefsExpansion}
                        getRowId={getRowId}
                        getRowClass={getExpansionRowClass}
                        context={gridContext}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        isRowSelectable={(node) => node.data?._rowType !== "group"}
                        getRowHeight={(params) =>
                          params.data?._rowType === "group" &&
                          params.data?._groupBy === EXPANSION_GROUP_BY
                            ? 56
                            : undefined
                        }
                        defaultColDef={{
                          isCellEditable: (p) => p.data?._rowType !== "group",
                          sortable: true,
                          filter: true,
                          resizable: true,
                          minWidth: 80,
                        }}
                        onGridReady={onExpansionGridReady}
                        onFirstDataRendered={onContentFitColumnsDataRendered}
                        onSelectionChanged={onAnySelectionChanged}
                        onFilterChanged={onExpansionFilterChanged}
                        onSortChanged={onExpansionSortChanged}
                        postSortRows={postSortExpansionRows}
                        onColumnVisible={onColumnVisible}
                        onHeaderContextMenu={onHeaderContextMenu}
                        onCellValueChanged={onProjectCellValueChanged}
                      />
                    </div>
                  </section>
                </div>
              </>
            )}
          />
          <Route path="plugins" element={<PluginsPage />} />
          <Route path="shop" element={<MarketplaceShopGate />} />
          <Route
            path="contracts"
            element={<ContractsGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="contracts/:id"
            element={<ContractsGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="crm"
            element={<CrmGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="crm/:tab"
            element={<CrmGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="crm/:tab/:id"
            element={<CrmGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="interconnects"
            element={<InterconnectsGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="interconnects/:id"
            element={<InterconnectsGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="reporting"
            element={<ReportingGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="materials-inventory"
            element={<MaterialsInventoryGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="datacenter"
            element={<DatacenterLayoutGate getAuthHeaders={getAuthHeaders} />}
          />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="admin/users" element={<AdminUsersPage />} />
          <Route path="admin/phase-history" element={<ProjectPhaseHistoryGate />} />
          <Route path="mail-log" element={<OutgoingMailLogGate />} />
          <Route path="messages" element={<CommentInboxPage />} />
          <Route
            path="construction"
            element={<ConstructionBoardGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="commercial-map"
            element={<CommercialLocationsMapGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="add-project"
            element={<AddProjectGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
          <Route
            path="add-report"
            element={<InspectorAddReportGate apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
          />
      </Route>
    </Routes>
    <InspectorDailyFootageModal
      apiUrl={API_URL}
      getAuthHeaders={getAuthHeaders}
      enabled={canAccessExpansion}
    />
    </>
  );
}


export default App;
